/**
 * IntakeModule — the ONE reusable per-module renderer for the intake module
 * library. Built ONCE; a service is just a SELECTION of module keys (resolved
 * through `lib/intakeCatalog.ts`). The stage engine renders a stage by mapping
 * its `moduleKeys` through THIS component — no service-specific form anywhere.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  REAL modules (identity_property, person_birth_data, presenting_concerns,
 *  goals_aspirations, implementation_timeline) render their inputs against the
 *  shared `IntakeForm` state and persist via the locked `answers` contract
 *  (intake.ts). `consent_permissions` is rendered by ConsentStage upstream (the
 *  engine's consent path), NOT here.
 *
 *  SCAFFOLD modules (everything else in MODULE_LIBRARY marked impl:'scaffold')
 *  render a minimal real capture from their `scaffoldFields` and persist to the
 *  sibling `intake_extras.{key}` field — NEVER into `answers`. They carry the
 *  field, no engine wiring. Promote a scaffold → real by adding owned `answers`
 *  fields + an intake.ts builder, with NO catalog or component change.
 *
 *  STATE OWNERSHIP. This component is presentational. The owning route
 *  (Intake.tsx) holds `form` + `extras` state and passes the mutators down.
 *  A read-only stage passes `readOnly` and the component renders a summary.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Btn3 } from './ui';
import { Combobox, DateMask, TimeMask } from './IntakeInputs';
import { searchCountries } from '../lib/countries';
import { computeOrgLevels, EXTERNAL_ROLES } from '../lib/orgChartLevels';
import { FLOOR_LABELS, FLOOR_IDS } from '../lib/propertyModel';
import { paletteFileFor } from '../lib/auoPalette';
import {
  parseGoogleMapsUrl,
  parseCoordPair,
  formatCoordPair,
  googleMapsUrl,
  googleEarthUrl,
} from '../lib/siteLocation';
import {
  moduleByKey,
  type ModuleDef,
  type ScaffoldField,
} from '../lib/intakeModules';
import { hidesArchitectContact, singleDrawingSet } from '../lib/serviceFlags';
import { getMergedConcerns, type ConcernItem } from '../data/concerns_checklist';
import {
  PROJECT_TYPES,
  INTENDED_USES,
  LANGUAGES,
  intendedUseIsFamily,
  personsMissingBirthData,
  blankProperty,
  blankFloor,
  blankPerson,
  blankDrawings,
  blankDrawingVersion,
  DRAWING_STAGES,
  newRowId,
  drawingsToPropertyInput,
  applyPropertyPatchToDrawings,
  type IntakeForm,
  type ProjectType,
  type Language,
  type ConcernInput,
  type PersonInput,
  type PropertyInput,
  type FloorInput,
  type MediaInput,
  type DrawingsInput,
  type DrawingVersion,
  type DrawingStage,
} from '../lib/intake';
import {
  RELATION_TO_CLIENT_GROUPS,
  RELATION_TO_CLIENT_VALUES,
  relationToClientLabel,
  WORK_ROLE_GROUPS,
  WORK_ROLE_VALUES,
  DEPARTMENT_VALUES,
  workAxisLabel,
} from '../types/personModel';
import {
  type DesignProgrammeInput,
  type SpaceRow,
  type AdjacencyRule,
  type CoordinationMember,
  type DesignBriefSectionFlags,
  blankSpaceRow,
  blankAdjacency,
  blankCoordinationMember,
  blankDesignProgramme,
  spaceScheduleTotal,
  designBriefFlagsForTier,
  sectionShown,
  sectionRequired,
  SPACE_PRIORITIES,
  HOUSEHOLD_TYPES,
  WFH_OPTIONS,
  HOSTING_OPTIONS,
  FUTURE_PROOFING,
  SPECIAL_FUNCTIONS,
  ARCH_STYLES,
  INTERIOR_LEANINGS,
  MATERIAL_PREFS,
  SCOPE_OF_WORK,
  BUDGET_RANGES,
  PHASING_OPTIONS,
  START_OPTIONS,
  CONSTRAINT_CHECKS,
  ADJACENCY_KINDS,
  COORDINATION_ROLES,
} from '../lib/designProgramme';
import ContactSlotLink from './ContactSlotLink';
import { listenCoordinationRoles, addCoordinationRole } from '../lib/coordinationRoles';
import { listenClients } from '../lib/projectStore';
import type { ContactLite } from '../lib/contactMatch';
import {
  type ConstructionDetailsInput,
  blankConstructionDetails,
  DRAWING_STAGE_OPTS, STRUCTURAL_SYSTEMS, PREPARED_CHECKS, WATER_SOURCES, YES_NO,
  ELEC_SOURCES, BACKUP_POWER, SOLAR_PROVISION, HVAC_APPROACH, FALSE_CEILING,
  LIGHTING_APPROACH, SPEC_LEVEL, PLUMB_GRADE, ELEC_GRADE, WINDOW_SYSTEM, DOOR_TYPES,
  SOIL_TYPES, GEOTECH, WATER_TABLE, CONSTRUCTION_METHOD, STATUTORY_APPROVAL,
  SITE_CONDITION, TARGET_START,
} from '../lib/constructionDetails';
import {
  type DesignPlansPrefsInput,
  blankDesignPlansPrefs,
  HAVE_PLAN_OPTS, DECIDER_ROLE_OPTS, APPROVAL_FLOW_OPTS, VASTU_STRICTNESS_OPTS,
} from '../lib/designPlansPrefs';
import { geocode, countryCodeHint } from '../lib/geocoder';
import { generateThumbnail, mediaKindOf, defaultMime } from '../lib/mediaThumb';
import PropertySlotLink from './PropertySlotLink';
import { listenAllProperties } from '../lib/propertyStore';
import type { LiveProperty } from '../lib/propertyModel';
import type { IntakeScope } from '../lib/intakeScope';

/** Title-case an intended-use / project-type enum value for display. */
function useLabel(t: string): string {
  if (!t) return '';
  return t
    .split('_')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

/** The canonical floor labels (combobox options for floor tags). */
const FLOOR_OPTIONS: string[] = [
  'Outside / periphery',
  ...FLOOR_IDS.map((id) => FLOOR_LABELS[id]),
];

/** Common place/room options (combobox; client can add their own). */
const PLACE_OPTIONS: string[] = [
  'Front / exterior',
  'Main entrance',
  'Living / hall',
  'Kitchen',
  'Master bedroom',
  'Other bedroom',
  'Toilet / bathroom',
  'Pooja room',
  'Office / cabin',
  'Reception',
  'Storage',
  'Staircase',
  'Backyard / open',
];

/* ── structured concerns checklist: category render order + titles ── */

/** Canonical category render order + client-facing titles for the structured
 *  presenting-concerns checklist. Any category not in this map still renders
 *  (appended in first-seen order) under its raw key. */
const CONCERN_CATEGORY_ORDER: string[] = [
  'financial',
  'health',
  'mental',
  'relationship',
  'career',
  'business',
  'progeny',
  'legal',
  'other',
];

const CONCERN_CATEGORY_TITLE: Record<string, string> = {
  financial: 'Financial',
  health: 'Health',
  mental: 'Mind & wellbeing',
  relationship: 'Relationships',
  career: 'Career',
  business: 'Business',
  progeny: 'Children & family',
  legal: 'Legal',
  other: 'Other',
};

/** Order the category keys present in the list by CONCERN_CATEGORY_ORDER, then
 *  any unknown categories in first-seen order. */
function orderedConcernCategories(items: ConcernItem[]): string[] {
  const seen: string[] = [];
  for (const it of items) if (!seen.includes(it.category)) seen.push(it.category);
  const known = CONCERN_CATEGORY_ORDER.filter((c) => seen.includes(c));
  const unknown = seen.filter((c) => !CONCERN_CATEGORY_ORDER.includes(c));
  return [...known, ...unknown];
}

/* ── shared field primitives (semantic tokens, pure-white, mobile-first) ── */

const fieldStyle: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ink)',
  margin: '0 0 4px',
};
const optTag = (
  <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ink-mute)', marginLeft: 6 }}>
    optional
  </span>
);

function Field({
  label,
  optional,
  hint,
  children,
}: {
  label: string;
  optional?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && (
        <label style={labelStyle}>
          {label}
          {optional && optTag}
        </label>
      )}
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function ReadRow({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 12.5, margin: '3px 0' }}>
      <span style={{ color: 'var(--ink-mute)', flex: '0 0 150px' }}>{k}</span>
      <span style={{ color: 'var(--ink)' }}>{v || '—'}</span>
    </div>
  );
}

const SCAFFOLD_BADGE = (
  <span
    title="Captured now; deeper engine wiring is staged for a later release."
    style={{
      fontSize: 10,
      fontWeight: 600,
      color: 'var(--ink-mute)',
      border: '1px solid var(--rule)',
      borderRadius: 'var(--r-sm)',
      padding: '1px 6px',
      marginLeft: 8,
    }}
  >
    basic capture
  </span>
);

/* ───────────────────────────── props ───────────────────────────── */

/** The handlers the owning route passes down for the REAL modules. */
export interface IntakeModuleHandlers {
  patch: (p: Partial<IntakeForm>) => void;
  setConcern: (i: number, c: Partial<ConcernInput>) => void;
  addConcern: (text?: string) => void;
  removeConcern: (i: number) => void;
  setGoal: (i: number, v: string) => void;
  addGoal: () => void;
  removeGoal: (i: number) => void;
  setPerson: (i: number, p: Partial<PersonInput>) => void;
  addPerson: () => void;
  removePerson: (i: number) => void;
  /** scaffold extras: get/set a value under intake_extras.{moduleKey}.{name} */
  getExtra: (moduleKey: string, name: string) => string | boolean;
  setExtra: (moduleKey: string, name: string, value: string | boolean) => void;
}

export interface IntakeModuleProps {
  /** spine module_registry key — selects the renderer. */
  moduleKey: string;
  form: IntakeForm;
  readOnly: boolean;
  handlers: IntakeModuleHandlers;
  /**
   * M5-S5 — is the viewer a STAFF member (vs the client self-filling)? Gates the
   * staff-only participant type (`external_professional`) and the staff-only
   * "Request birth details" action in the People module. Defaults to false
   * (client) so the client flow is unchanged for every other caller.
   */
  viewerIsStaff?: boolean;
  /**
   * M5-S5 — staff-only callback: "Request birth details" for the person at index
   * `i` (mints a birth_only intake link targeted at that person). Only wired in
   * the staff portal; absent on the client self-fill path.
   */
  onRequestBirth?: (i: number) => void;
  /**
   * L1C/L2C (2026-06-23) — the active SERVICE tier key (e.g. 'L1C', 'L2C'). Used
   * by `drawings_upload` to enforce the architect-coordination bright line: on
   * L1C the architect-contact fields are HIDDEN (we don't contact the architect);
   * on L2C they show. Optional — defaults to no tier (all fields shown).
   */
  serviceTier?: string;
  /**
   * AD-036 (rev 2026-06-24) — STAFF-ONLY registry context for the unified
   * Property-details step. When present, each property slot renders the inline
   * link-or-new control (PropertySlotLink) at its top, so a slot is either
   * linked from `properties/{P#####}` or created+registered on save. Absent on
   * the client self-fill path (clients never touch the registry).
   */
  propertyRegistry?: PropertyRegistryCtx;
}

/** AD-036 — registry context threaded into the property-details module. */
export interface PropertyRegistryCtx {
  scope: IntakeScope;
  tier: string;
  actorUid: string;
  onMsg: (m: string, ok?: boolean) => void;
  /** Proximity radius override; defaults to 25 m inside PropertySlotLink. */
  radiusM?: number;
}

/* ───────────────────────── M5-S5 participant types ───────────────────────── */

/**
 * The participant relationship taxonomy (M5-S5). The first three are
 * CLIENT-VISIBLE; `external_professional` is STAFF-ONLY (an architect / interior
 * designer / contractor the team coordinates with, not a household member). The
 * value is stored on the person doc's `relationship` field (free-string in the
 * AD-019 schema, so these are a controlled vocabulary over it — legacy free-text
 * values still load + display).
 */
export const PARTICIPANT_TYPES_CLIENT: { value: string; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'co_owner', label: 'Co-owner' },
  { value: 'family_member', label: 'Family member' },
  { value: 'tenant', label: 'Tenant' },
];
export const PARTICIPANT_TYPE_STAFF = {
  value: 'external_professional',
  label: 'External professional (staff only)',
};

/** Human label for a stored relationship value (controlled or legacy free text). */
export function participantLabel(value: string): string {
  const all = [...PARTICIPANT_TYPES_CLIENT, PARTICIPANT_TYPE_STAFF];
  return all.find((t) => t.value === value)?.label || value;
}

/* ───────────────────────────── renderer ───────────────────────────── */

export function IntakeModule({
  moduleKey,
  form,
  readOnly,
  handlers,
  viewerIsStaff = false,
  onRequestBirth,
  serviceTier,
  propertyRegistry,
}: IntakeModuleProps) {
  const def = moduleByKey(moduleKey);
  if (!def) {
    return (
      <Box title={moduleKey} note="Unknown module">
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
          This module key is not in the library.
        </p>
      </Box>
    );
  }
  // consent is rendered upstream by ConsentStage — never here.
  if (moduleKey === 'consent_permissions') return null;

  if (def.impl === 'real') {
    switch (moduleKey) {
      case 'identity_property':
        return <IdentityModule form={form} readOnly={readOnly} h={handlers} />;
      case 'person_birth_data':
        return (
          <PeopleModule
            form={form}
            readOnly={readOnly}
            h={handlers}
            viewerIsStaff={viewerIsStaff}
            onRequestBirth={onRequestBirth}
          />
        );
      case 'presenting_concerns':
        return <ConcernsModule form={form} readOnly={readOnly} h={handlers} />;
      case 'goals_aspirations':
        return <GoalsModule form={form} readOnly={readOnly} h={handlers} />;
      case 'implementation_timeline':
        return <NextStepsModule form={form} readOnly={readOnly} h={handlers} />;
      // ── L1A redesign renderers ──
      case 'l1a_about_you':
        return <L1AAboutYou form={form} readOnly={readOnly} h={handlers} />;
      case 'l1b_about_you':
        return <L1AAboutYou form={form} readOnly={readOnly} h={handlers} showPropertyAddress />;
      case 'l1a_goal_use':
        return <L1AGoalUse form={form} readOnly={readOnly} h={handlers} serviceTier={serviceTier} />;
      case 'l1a_concerns':
        return <L1AConcerns form={form} readOnly={readOnly} h={handlers} />;
      case 'l1a_property_details':
        return <L1APropertyDetails form={form} readOnly={readOnly} h={handlers} registry={propertyRegistry} />;
      case 'l1b_property_details':
        return <L1APropertyDetails form={form} readOnly={readOnly} h={handlers} mode="l1b" registry={propertyRegistry} />;
      case 'l1b_plans_preferences':
        return <L1BPlansPreferences form={form} readOnly={readOnly} h={handlers} />;
      case 'drawings_upload':
        return <DrawingsUploadModule form={form} readOnly={readOnly} h={handlers} serviceTier={serviceTier} registry={propertyRegistry} />;
      case 'design_programme':
        return <DesignProgrammeModule form={form} readOnly={readOnly} h={handlers} serviceTier={serviceTier} />;
      case 'construction_details':
        return <ConstructionDetailsModule form={form} readOnly={readOnly} h={handlers} />;
      case 'l3b_plans_preferences':
        return <L3BPlansPreferences form={form} readOnly={readOnly} h={handlers} />;
      case 'l1a_people':
        return (
          <L1APeople
            form={form}
            readOnly={readOnly}
            h={handlers}
            viewerIsStaff={viewerIsStaff}
          />
        );
      default:
        // a real module without a wired renderer falls back to scaffold render
        return <ScaffoldModule def={def} readOnly={readOnly} h={handlers} serviceTier={serviceTier} />;
    }
  }
  return <ScaffoldModule def={def} readOnly={readOnly} h={handlers} serviceTier={serviceTier} />;
}

/* ───────────────────────── REAL: identity ───────────────────────── */

function IdentityModule({
  form,
  readOnly,
  h,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
}) {
  if (readOnly) {
    return (
      <Box title="Property & identity" note="Saved">
        <ReadRow k="Your name" v={form.client_name} />
        {form.firm && <ReadRow k="Company / firm" v={form.firm} />}
        <ReadRow k="Property address" v={form.property_address} />
        <ReadRow k="Project type" v={form.project_type} />
        <ReadRow k="Country" v={form.country} />
        {form.scope_label && <ReadRow k="Scope" v={form.scope_label} />}
        <ReadRow k="Report language" v={form.language === 'en' ? 'English' : 'Hindi'} />
      </Box>
    );
  }
  return (
    <Box title="Property & identity">
      <Field label="Your name">
        <input
          style={fieldStyle}
          value={form.client_name}
          onChange={(e) => h.patch({ client_name: e.target.value })}
          placeholder="Name shown on the report"
        />
      </Field>
      <Field label="Company / firm" optional>
        <input
          style={fieldStyle}
          value={form.firm}
          onChange={(e) => h.patch({ firm: e.target.value })}
          placeholder="For a commercial property"
        />
      </Field>
      <Field label="Property address">
        <textarea
          style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }}
          value={form.property_address}
          onChange={(e) => h.patch({ property_address: e.target.value })}
          placeholder="Full site address"
        />
      </Field>
      <Field label="Project type">
        <select
          style={fieldStyle}
          value={form.project_type}
          onChange={(e) => h.patch({ project_type: e.target.value as ProjectType | '' })}
        >
          <option value="">Select…</option>
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t
                .split('_')
                .map((w) => w[0].toUpperCase() + w.slice(1))
                .join(' ')}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Country" hint="Drives the region for remedy links.">
        <input
          style={fieldStyle}
          value={form.country}
          onChange={(e) => h.patch({ country: e.target.value })}
        />
      </Field>
      <Field label="Scope" optional hint="One line — e.g. “3rd-floor working bay”.">
        <input
          style={fieldStyle}
          value={form.scope_label}
          onChange={(e) => h.patch({ scope_label: e.target.value })}
        />
      </Field>
      <Field label="Report language">
        <select
          style={fieldStyle}
          value={form.language}
          onChange={(e) => h.patch({ language: e.target.value as Language })}
        >
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {l === 'en' ? 'English' : 'Hindi'}
            </option>
          ))}
        </select>
      </Field>
    </Box>
  );
}

/* ──────────────────── M5-S6b: Axis-3 Work / reporting (AD-027) ──────────────────── */

/**
 * The work/business project types (everything except `residence`). Used to GATE
 * the Work/reporting group's default open-state: a work context auto-expands it;
 * a residence keeps it collapsed (but still reachable for a family business).
 */
const WORK_PROJECT_TYPES: readonly string[] = [
  'office',
  'commercial',
  'retail',
  'industrial',
  'institutional',
];

/**
 * Axis-3 Work / reporting fields for the person at index `i`:
 *   • work_role  (select, grouped vocab)
 *   • reports_to (select — populated from the OTHER people in the intake, excludes
 *                 self; a soft cycle warning when the chosen manager already
 *                 reports back to this person)
 *   • department (select)
 * Rendered inside a <details> collapsible. The collapsible is OPEN by default on a
 * work-context project type (so office/commercial intakes see it immediately) and
 * CLOSED by default on a residence (never forced on residential users; still
 * reachable for a family-business residence).
 */
function WorkReportingGroup({
  form,
  i,
  h,
}: {
  form: IntakeForm;
  i: number;
  h: IntakeModuleHandlers;
}) {
  const p = form.persons[i];
  const isWorkContext = WORK_PROJECT_TYPES.includes(form.project_type);
  // Other people in the intake, by stable row id — the candidate managers.
  const others = form.persons
    .map((q, qi) => ({ q, qi }))
    .filter(({ qi }) => qi !== i && (form.persons[qi].name || '').trim());
  const mgr = p.reports_to ? form.persons.find((q) => q.id === p.reports_to) : undefined;
  // Soft cycle warning: the chosen manager directly reports back to this person.
  const directCycle = !!(mgr && mgr.reports_to && mgr.reports_to === p.id);
  // A stale ref (manager row was removed/renamed-away) — surface as a one-off option
  // so the value is never silently dropped.
  const staleRef = !!(p.reports_to && !mgr);

  const hasAny = !!(p.work_role || p.reports_to || p.department);
  // OPEN by default on a work-context type; otherwise collapsed (unless a value is
  // already set, in which case open so the user sees what was captured).
  const defaultOpen = isWorkContext || hasAny;

  return (
    <details open={defaultOpen} style={{ marginTop: 8 }}>
      <summary
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--ink-mute)',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        Work / reporting details {optTag}
      </summary>
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px dashed var(--rule)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 8,
        }}
      >
        <div>
          <label style={labelStyle}>Work role {optTag}</label>
          <select
            style={fieldStyle}
            value={p.work_role || ''}
            onChange={(e) => h.setPerson(i, { work_role: e.target.value })}
          >
            <option value="">Select…</option>
            {WORK_ROLE_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.values.map((v) => (
                  <option key={v} value={v}>
                    {workAxisLabel(v)}
                  </option>
                ))}
              </optgroup>
            ))}
            {p.work_role &&
              !WORK_ROLE_VALUES.includes(
                p.work_role as (typeof WORK_ROLE_VALUES)[number],
              ) && <option value={p.work_role}>{p.work_role}</option>}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Reports to {optTag}</label>
          {/* The org-chart edge: another person in this intake. Self is excluded
              (can't be your own manager). A soft cycle warning shows when the
              chosen manager directly reports back to this person. */}
          <select
            style={fieldStyle}
            value={p.reports_to || ''}
            onChange={(e) => h.setPerson(i, { reports_to: e.target.value })}
          >
            <option value="">— none —</option>
            {others.map(({ q }) => (
              <option key={q.id} value={q.id}>
                {(q.name || '—').trim()}
              </option>
            ))}
            {staleRef && <option value={p.reports_to}>{p.reports_to}</option>}
          </select>
          {directCycle && (
            <div style={{ fontSize: 11, color: 'var(--warn, #b45309)', marginTop: 3 }}>
              {(mgr?.name || 'That person').trim()} also reports to{' '}
              {(p.name || 'this person').trim()} — please double-check the reporting line.
            </div>
          )}
        </div>

        <div>
          <label style={labelStyle}>Department {optTag}</label>
          <select
            style={fieldStyle}
            value={p.department || ''}
            onChange={(e) => h.setPerson(i, { department: e.target.value })}
          >
            <option value="">Select…</option>
            {DEPARTMENT_VALUES.map((v) => (
              <option key={v} value={v}>
                {workAxisLabel(v)}
              </option>
            ))}
            {p.department &&
              !DEPARTMENT_VALUES.includes(
                p.department as (typeof DEPARTMENT_VALUES)[number],
              ) && <option value={p.department}>{p.department}</option>}
          </select>
        </div>
      </div>
    </details>
  );
}

/* ───────────────────────── REAL: people ───────────────────────── */

function PeopleModule({
  form,
  readOnly,
  h,
  viewerIsStaff = false,
  onRequestBirth,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
  viewerIsStaff?: boolean;
  onRequestBirth?: (i: number) => void;
}) {
  const astroWarnings = personsMissingBirthData(form);
  if (readOnly) {
    return (
      <Box title="People" note="Saved">
        {form.persons.length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>No people added.</p>
        )}
        {form.persons.map((p) => {
          // AD-027 Axis-3 read-only: "Work-role · Department → reports to {name}".
          const mgr = p.reports_to
            ? form.persons.find((q) => q.id === p.reports_to)
            : undefined;
          const workBits = [
            p.work_role ? workAxisLabel(p.work_role) : '',
            p.department ? workAxisLabel(p.department) : '',
            mgr ? `reports to ${mgr.name || '—'}` : '',
          ]
            .filter(Boolean)
            .join(' · ');
          // Astrology details surfaced in read-only/preview (owner ask
          // 2026-06-22): date · time · place of birth + coordinates.
          const birth = [
            (p.dob || '').trim() ? `DOB ${p.dob}` : '',
            (p.time_of_birth || '').trim() ? `Time ${p.time_of_birth}` : '',
            (p.place_of_birth || '').trim() ? `Place ${p.place_of_birth}` : '',
            (p.lat || '').trim() && (p.lon || '').trim() ? `(${p.lat}, ${p.lon})` : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div key={p.id}>
              <ReadRow
                k={p.name || '—'}
                v={[
                  p.role ? participantLabel(p.role) : '',
                  p.relation_to_client ? relationToClientLabel(p.relation_to_client) : '',
                  workBits,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
              {birth && <ReadRow k="" v={`🎂 ${birth}`} />}
              {(p.health_concerns || '').trim() && (
                <ReadRow k="" v={`health: ${p.health_concerns!.trim()}`} />
              )}
            </div>
          );
        })}
        <ReadRow k="Per-person astrology" v={form.astro_in_scope ? 'Included' : 'Not included'} />
      </Box>
    );
  }
  return (
    <Box title="People" note="Who else lives or works at this property?">
      <Field label="">
        <label
          style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={form.astro_in_scope}
            onChange={(e) => h.patch({ astro_in_scope: e.target.checked })}
          />
          Include per-person astrology in this report
        </label>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 3 }}>
          Turn on only if birth details are available. Birth fields are optional — anyone without
          complete details is simply saved without an astrology reading.
        </div>
      </Field>

      {form.persons.length === 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
          Add the people who live or work here.
        </p>
      )}

      {form.persons.map((p, i) => (
        <div
          key={p.id}
          style={{
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            padding: 10,
            marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '2 1 180px' }}>
              <label style={labelStyle}>Name</label>
              <input
                style={fieldStyle}
                value={p.name}
                onChange={(e) => h.setPerson(i, { name: e.target.value })}
              />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <label style={labelStyle}>Relationship {optTag}</label>
              {/* M5-S5: typed participant select. Client sees 3 types; staff also
                  see `external_professional`. A legacy free-text value (not in
                  the vocabulary) is preserved as a one-off option so it is not
                  silently dropped on edit. */}
              <select
                style={fieldStyle}
                value={p.role || ''}
                onChange={(e) => h.setPerson(i, { role: e.target.value })}
              >
                <option value="">Select…</option>
                {PARTICIPANT_TYPES_CLIENT.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
                {viewerIsStaff && (
                  <option value={PARTICIPANT_TYPE_STAFF.value}>
                    {PARTICIPANT_TYPE_STAFF.label}
                  </option>
                )}
                {p.role &&
                  ![
                    ...PARTICIPANT_TYPES_CLIENT.map((t) => t.value),
                    PARTICIPANT_TYPE_STAFF.value,
                  ].includes(p.role) && (
                    <option value={p.role}>{p.role}</option>
                  )}
              </select>
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <label style={labelStyle}>Relationship to primary occupant {optTag}</label>
              {/* M5-S6 (AD-027): Axis-2 relation_to_client — the SPECIFIC familial
                  / business relation to the primary client. Drives relational
                  astrology (compute DEFERRED). Grouped optgroups; a legacy
                  free-text value is preserved as a one-off option. */}
              <select
                style={fieldStyle}
                value={p.relation_to_client || ''}
                onChange={(e) => h.setPerson(i, { relation_to_client: e.target.value })}
              >
                <option value="">Select…</option>
                {RELATION_TO_CLIENT_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.values.map((v) => (
                      <option key={v} value={v}>
                        {relationToClientLabel(v)}
                      </option>
                    ))}
                  </optgroup>
                ))}
                {p.relation_to_client &&
                  !RELATION_TO_CLIENT_VALUES.includes(
                    p.relation_to_client as (typeof RELATION_TO_CLIENT_VALUES)[number],
                  ) && (
                    <option value={p.relation_to_client}>{p.relation_to_client}</option>
                  )}
              </select>
            </div>
            <Btn3 intent="ghost" size="sm" onClick={() => h.removePerson(i)} aria-label="Remove person">
              Remove
            </Btn3>
          </div>

          {/* M5-S6b (AD-027): Axis-3 Work / reporting group. GATING — when the
              project type is a work/business context (office · commercial · retail ·
              industrial · institutional) the group is auto-expanded (work context);
              on a residence it is a collapsed-by-default <details> so the org axis is
              never forced on residential users but is still reachable for a
              family-business residence. Independent + optional from the family axes. */}
          <WorkReportingGroup form={form} i={i} h={h} />

          {/* M5-S5: staff-only — request birth details for a person with no DOB.
              Mints a birth_only intake link targeted at this person. */}
          {viewerIsStaff && onRequestBirth && (p.name || '').trim() && !((p.dob || '').trim()) && (
            <div style={{ marginTop: 8 }}>
              <Btn3 intent="ghost" size="sm" onClick={() => onRequestBirth(i)}>
                Request birth details
              </Btn3>
              <span style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--ink-mute)' }}>
                Sends {(p.name || 'this person').trim()} a link to add their date, time &amp; place of birth.
              </span>
            </div>
          )}

          {form.astro_in_scope && (
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px dashed var(--rule)',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 8,
              }}
            >
              <div>
                <label style={labelStyle}>Date of birth {optTag}</label>
                <input
                  type="date"
                  style={fieldStyle}
                  value={p.dob || ''}
                  onChange={(e) => h.setPerson(i, { dob: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Time of birth {optTag}</label>
                <input
                  type="time"
                  style={fieldStyle}
                  value={p.time_of_birth || ''}
                  onChange={(e) => h.setPerson(i, { time_of_birth: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Place of birth {optTag}</label>
                <input
                  style={fieldStyle}
                  value={p.place_of_birth || ''}
                  onChange={(e) => h.setPerson(i, { place_of_birth: e.target.value })}
                  placeholder="City, country"
                />
              </div>
              <div>
                <label style={labelStyle}>Birth latitude {optTag}</label>
                <input
                  style={fieldStyle}
                  value={p.lat || ''}
                  onChange={(e) => h.setPerson(i, { lat: e.target.value })}
                  placeholder="e.g. 22.7196"
                  inputMode="decimal"
                />
              </div>
              <div>
                <label style={labelStyle}>Birth longitude {optTag}</label>
                <input
                  style={fieldStyle}
                  value={p.lon || ''}
                  onChange={(e) => h.setPerson(i, { lon: e.target.value })}
                  placeholder="e.g. 75.8577"
                  inputMode="decimal"
                />
              </div>
              <div>
                <label style={labelStyle}>Time zone {optTag}</label>
                <input
                  style={fieldStyle}
                  value={p.tz || ''}
                  onChange={(e) => h.setPerson(i, { tz: e.target.value })}
                  placeholder="e.g. Asia/Kolkata"
                />
              </div>
            </div>
          )}
          {/* STANDARD per-person Health module (2026-06-23) — directly below the
              birth details, on every astrology-bearing template. Optional &
              private; always shown (independent of astrology). */}
          <div style={{ marginTop: 10 }}>
            <label style={labelStyle}>Health concerns for this person {optTag}</label>
            <input
              style={fieldStyle}
              value={p.health_concerns || ''}
              onChange={(e) => h.setPerson(i, { health_concerns: e.target.value })}
              placeholder="e.g. recurring anxiety, sleep trouble, BP — anything you'd like the reading to keep in view"
              aria-label="Health concerns for this person"
            />
            <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 3 }}>
              Per-person &amp; read alongside their astrology. Shared only if you wish. We never
              diagnose or prescribe.
            </div>
          </div>
        </div>
      ))}
      <Btn3 intent="ink" size="sm" onClick={h.addPerson}>
        + Add person
      </Btn3>

      {astroWarnings.length > 0 && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: 'var(--ink-mute)',
            background: 'var(--surface)',
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            padding: 8,
          }}
        >
          These people don't yet have complete birth details and will be saved without an astrology
          reading: <strong>{astroWarnings.join(', ')}</strong>.
        </div>
      )}
    </Box>
  );
}

/* ───────────────────────── REAL: concerns ───────────────────────── */

/**
 * Presenting concerns — STRUCTURED 32-item grouped checklist (M5-concerns
 * fold-in, 2026-06-20). The client ticks the life areas they want the analysis
 * to focus on; collapsible category groups; an "Other" free-text field for
 * anything not in the list.
 *
 * STORAGE = the locked `answers.presenting_concerns[]` contract — NOT the
 * project-scoped `projects/{pid}/concerns/` path (retired). Each ticked item
 * persists as `{ text: <label>, concern_id: <id> }`; the "Other" free text
 * persists as a single trailing `{ text }` WITHOUT a `concern_id`. The shape is
 * byte-identical to what the engine + report template already consume — the
 * engine's footnote join already keys on `concern_id` (intake.ts §concernToAnswer).
 *
 * `zone_correlated` is NOT written onto the answer (keeps the contract intact);
 * it is RECOVERABLE downstream by re-merging the palette via
 * `getMergedConcerns()` and looking the `concern_id` up (absent === correlated).
 *
 * "Amber" items (`zone_correlated === false`) get NO visual distinction for the
 * client — identical to every other row.
 *
 * Concerns are OPTIONAL: selecting nothing is valid (the stage stays complete /
 * the Continue button is never blocked by this module).
 */
function ConcernsModule({
  form,
  readOnly,
  h,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
}) {
  const projectType = form.project_type || 'residence';
  const [items, setItems] = useState<ConcernItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMergedConcerns(projectType)
      .then((list) => {
        if (!alive) return;
        setItems(list);
        const cats = orderedConcernCategories(list);
        // First category open by default; the rest collapsed.
        setOpenCats((prev) => {
          if (Object.keys(prev).length) return prev;
          const open: Record<string, boolean> = {};
          cats.forEach((c, i) => {
            open[c] = i === 0;
          });
          return open;
        });
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [projectType]);

  const byCategory = useMemo(() => {
    const map = new Map<string, ConcernItem[]>();
    for (const it of items) {
      const arr = map.get(it.category) || [];
      arr.push(it);
      map.set(it.category, arr);
    }
    return map;
  }, [items]);
  const cats = useMemo(() => orderedConcernCategories(items), [items]);

  // The set of palette `concern_id`s known in this build — distinguishes a
  // structured checklist row from a free-text "Other"/legacy concern.
  const knownIds = useMemo(
    () => new Set(items.map((it) => it.concern_id)),
    [items],
  );

  // Currently-selected palette ids, read straight off the answer array.
  const selectedIds = useMemo(() => {
    const s = new Set<string>();
    for (const c of form.presenting_concerns) {
      if (c.concern_id) s.add(c.concern_id);
    }
    return s;
  }, [form.presenting_concerns]);

  // The "Other" / free-text concern: the first answer row WITHOUT a concern_id.
  // (Pre-fold-in docs stored every concern as free text — those load here too,
  //  so nothing the client typed before is ever silently dropped.)
  const otherEntry = form.presenting_concerns.find((c) => !c.concern_id);
  const otherText = otherEntry?.text || '';

  /** Rebuild the whole answer array from a desired set of selected ids + the
   *  free-text "Other" value, preserving each row's existing severity. */
  function commit(nextSelected: Set<string>, nextOther: string) {
    const prevById = new Map(
      form.presenting_concerns
        .filter((c) => c.concern_id)
        .map((c) => [c.concern_id as string, c]),
    );
    // Checklist rows, in palette order, only for selected ids.
    const rows: ConcernInput[] = items
      .filter((it) => nextSelected.has(it.concern_id))
      .map((it) => {
        const prev = prevById.get(it.concern_id);
        return {
          text: it.label,
          concern_id: it.concern_id,
          severity: prev?.severity ?? '',
        };
      });
    // Carry any selected id that is NOT in the current palette (e.g. a returning
    // client whose project type changed) so the selection is never lost.
    for (const id of nextSelected) {
      if (!knownIds.has(id) && prevById.has(id)) rows.push(prevById.get(id)!);
    }
    const other = nextOther.trim();
    if (other) rows.push({ text: other });
    h.patch({ presenting_concerns: rows });
  }

  function toggle(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    commit(next, otherText);
  }

  function toggleCat(cat: string) {
    setOpenCats((o) => ({ ...o, [cat]: !o[cat] }));
  }

  if (readOnly) {
    const labelFor = (c: ConcernInput) => c.text;
    return (
      <Box title="What's on your mind?" note="Saved">
        {form.presenting_concerns.length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>No concerns selected.</p>
        )}
        {form.presenting_concerns.map((c, i) => (
          <ReadRow key={i} k={labelFor(c)} v={c.severity || ''} />
        ))}
      </Box>
    );
  }

  return (
    <Box title="What's on your mind?" note="Select the areas you'd like the analysis to focus on">
      {loading ? (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cats.map((cat) => {
            const group = byCategory.get(cat) || [];
            if (!group.length) return null;
            const open = openCats[cat];
            return (
              <section
                key={cat}
                style={{ borderBottom: '1px solid var(--rule)', paddingBottom: 10 }}
              >
                <button
                  type="button"
                  onClick={() => toggleCat(cat)}
                  aria-expanded={!!open}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'var(--ink-mute)',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  <span>{CONCERN_CATEGORY_TITLE[cat] ?? cat}</span>
                  <span style={{ fontSize: 14 }}>{open ? '–' : '+'}</span>
                </button>
                {open && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                    {group.map((it) => {
                      const checked = selectedIds.has(it.concern_id);
                      return (
                        <label
                          key={it.concern_id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 10,
                            padding: '7px 9px',
                            borderRadius: 'var(--r-sm)',
                            borderLeft: checked
                              ? '3px solid var(--accent)'
                              : '3px solid transparent',
                            background: checked ? 'var(--accent-wash)' : 'transparent',
                            fontSize: 13.5,
                            color: 'var(--ink)',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(it.concern_id)}
                            style={{ marginTop: 2 }}
                          />
                          <span>{it.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}

          {/* Other — free text (client-verbatim, preserved as typed) */}
          <Field label="Something else" optional hint="Anything not in the list above.">
            <input
              style={fieldStyle}
              value={otherText}
              onChange={(e) => commit(selectedIds, e.target.value)}
              placeholder="Anything else you'd like us to focus on"
            />
          </Field>
        </div>
      )}
    </Box>
  );
}

/* ───────────────────────── REAL: goals ───────────────────────── */

function GoalsModule({
  form,
  readOnly,
  h,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
}) {
  if (readOnly) {
    return (
      <Box title="Goals" note="Saved">
        {form.goals.length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>No goals added.</p>
        )}
        {form.goals.map((g, i) => (
          <ReadRow key={i} k={i === 0 ? 'Goals' : ''} v={g} />
        ))}
      </Box>
    );
  }
  return (
    <Box title="Goals" note="What outcomes would success look like?">
      {form.goals.map((g, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input
            style={fieldStyle}
            value={g}
            onChange={(e) => h.setGoal(i, e.target.value)}
            placeholder="e.g. Steady wealth, harmony at home"
          />
          <Btn3 intent="ghost" size="sm" onClick={() => h.removeGoal(i)} aria-label="Remove goal">
            Remove
          </Btn3>
        </div>
      ))}
      <Btn3 intent="ink" size="sm" onClick={h.addGoal}>
        + Add goal
      </Btn3>
    </Box>
  );
}

/* ───────────────────────── REAL: next steps ───────────────────────── */

function NextStepsModule({
  form,
  readOnly,
  h,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
}) {
  if (readOnly) {
    return (
      <Box title="Next steps" note="Saved">
        <ReadRow k="Planned remedy start" v={form.follow_up_start_date} />
      </Box>
    );
  }
  return (
    <Box title="Next steps" note="Optional">
      <Field
        label="Planned start date for remedies"
        optional
        hint="Anchors the 21- and 44-day review calls."
      >
        <input
          type="date"
          style={fieldStyle}
          value={form.follow_up_start_date}
          onChange={(e) => h.patch({ follow_up_start_date: e.target.value })}
        />
      </Field>
    </Box>
  );
}

/* ═══════════════════════ L1A REDESIGN renderers (2026-06-21) ═══════════════════════ */

const reqMark = <span style={{ color: 'var(--danger, #dc2626)', fontWeight: 800 }}> *</span>;
const autoTag = (
  <span
    style={{
      background: 'var(--ok-wash, #dcfce7)',
      color: 'var(--ok-deep, #166534)',
      fontWeight: 700,
      fontSize: 10,
      padding: '1px 6px',
      borderRadius: 4,
      marginLeft: 6,
      textTransform: 'uppercase',
      letterSpacing: '.3px',
    }}
  >
    auto-filled
  </span>
);

/** A required-or-optional label header used by the L1A modules. */
function L1ALabel({
  children,
  required,
  optional,
  auto,
  fmt,
}: {
  children: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  auto?: boolean;
  fmt?: string;
}) {
  return (
    <label style={labelStyle}>
      {children}
      {required && reqMark}
      {optional && optTag}
      {auto && autoTag}
      {fmt && (
        <span
          style={{
            background: 'var(--accent-wash)',
            color: 'var(--accent)',
            fontWeight: 700,
            fontSize: 10.5,
            padding: '1px 6px',
            borderRadius: 4,
            marginLeft: 6,
          }}
        >
          {fmt}
        </span>
      )}
    </label>
  );
}

/* ───────────────────────── L1A · 1 — About you ───────────────────────── */

function L1AAboutYou({
  form,
  readOnly,
  h,
  showPropertyAddress = false,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
  /**
   * L1B (2026-06-23) — L1B is always the ONE space the client already occupies,
   * so the contact block also captures the required property address. L1A is
   * multi-property (each address is captured per-candidate in Property details),
   * so it leaves this OFF. The contact block is otherwise identical.
   */
  showPropertyAddress?: boolean;
}) {
  if (readOnly) {
    return (
      <Box title="About you" note="Saved">
        <ReadRow k="Your name" v={form.client_name} />
        {form.firm && <ReadRow k="Company / firm" v={form.firm} />}
        <ReadRow k="Email" v={form.email} />
        <ReadRow k="WhatsApp" v={form.whatsapp} />
        {form.address && <ReadRow k="Your address" v={form.address} />}
        <ReadRow k="Country" v={form.country} />
        <ReadRow k="Report language" v={form.language === 'en' ? 'English' : 'Hindi'} />
        {showPropertyAddress && (
          <ReadRow k="Property address" v={form.property_address} />
        )}
      </Box>
    );
  }
  return (
    <Box title="About you" note="About you and where the report is going." allowOverflow>
      <Field label="">
        <L1ALabel auto>Your name</L1ALabel>
        <input
          style={fieldStyle}
          value={form.client_name}
          onChange={(e) => h.patch({ client_name: e.target.value })}
          placeholder="Name shown on the report"
        />
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 3 }}>
          From your profile — edit spelling if needed.
        </div>
      </Field>
      <Field label="Company / firm" optional>
        <input
          style={fieldStyle}
          value={form.firm}
          onChange={(e) => h.patch({ firm: e.target.value })}
          placeholder="If this is for a business"
        />
      </Field>
      <Field label="">
        <L1ALabel required auto>Email</L1ALabel>
        <input
          style={fieldStyle}
          type="email"
          value={form.email}
          onChange={(e) => h.patch({ email: e.target.value })}
          placeholder="you@example.com"
        />
      </Field>
      <Field label="">
        <L1ALabel required auto>WhatsApp number</L1ALabel>
        <input
          style={fieldStyle}
          value={form.whatsapp}
          onChange={(e) => h.patch({ whatsapp: e.target.value })}
          placeholder="+91 80154 17121"
        />
      </Field>
      <Field label="Your address" optional hint="Your contact / home address — the property address is captured separately.">
        <input
          style={fieldStyle}
          value={form.address}
          onChange={(e) => h.patch({ address: e.target.value })}
          placeholder="Your contact / home address"
        />
      </Field>
      <Field label="">
        <L1ALabel required>Country</L1ALabel>
        <Combobox
          value={form.country}
          onChange={(v) => h.patch({ country: v })}
          options={searchCountries(form.country)}
          placeholder="Type to search…"
          ariaLabel="Country"
        />
      </Field>
      <Field label="Report language" hint="English for now. Hindi added once enough clients request it.">
        <select
          style={fieldStyle}
          value={form.language}
          onChange={(e) => h.patch({ language: e.target.value as Language })}
        >
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {l === 'en' ? 'English' : 'Hindi'}
            </option>
          ))}
        </select>
      </Field>
      {showPropertyAddress && (
        <Field
          label=""
          hint="L1B reviews one space you already occupy. Its exact location & north are captured in Property details."
        >
          <L1ALabel required>Address of the property being reviewed</L1ALabel>
          <input
            style={fieldStyle}
            value={form.property_address}
            onChange={(e) => h.patch({ property_address: e.target.value })}
            placeholder="Full address of the space you occupy and want reviewed"
            aria-label="Address of the property being reviewed"
          />
        </Field>
      )}
    </Box>
  );
}

/* ─────────────────── L1A · 2 — Goal & intended use ─────────────────── */

/**
 * L1AGoalUse — tier-aware goal/use question (2026-06-23).
 * The question text and sub-note differ by tier:
 *   L1A (default): "What are you selecting a property for?" — property selection mode.
 *   L1B:           "What type of property are we reviewing for you?" — existing occupied space.
 *   L1C / L2C:     "What uses are you planning in this property?" — proposed plan review.
 * The `serviceTier` prop is the raw tier key (e.g. 'L1B', 'L1C', 'L2C_R1'); the
 * renderer calls spineTierKey internally to normalise it.
 */
function L1AGoalUse({
  form,
  readOnly,
  h,
  serviceTier,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
  /**
   * The active service tier key (e.g. 'L1A', 'L1B', 'L1C', 'L2C'). When
   * provided, the question text and sub-note are adjusted for the service.
   * Defaults to L1A behaviour (property selection) when absent.
   */
  serviceTier?: string;
}) {
  // Normalise: strip revision suffix (L2C_R1 → L2C), uppercase.
  const tierKey = (serviceTier || '').replace(/_R\d+$/i, '').toUpperCase();

  /** Question heading shown above the use-chip grid. */
  const questionText: string =
    tierKey === 'L1B'
      ? 'What type of property are we reviewing for you?'
      : tierKey === 'L1C' || tierKey === 'L2C'
      ? 'What uses are you planning in this property?'
      : 'What are you selecting a property for?';

  /** One-line sub-note below the question heading. */
  const subNote: string =
    tierKey === 'L1B'
      ? 'Tell us about the existing space. Darshan diagnoses what is and recommends remedies — it does not redesign or select.'
      : tierKey === 'L1C' || tierKey === 'L2C'
      ? 'We review your proposed plan and provide Vastu guidance before construction begins.'
      : 'Your intended use — this tailors the questions, the checklist & the People chart.';

  /** Footer note inside the chip grid section. */
  const chipFooter: string =
    tierKey === 'L1B' || tierKey === 'L1C' || tierKey === 'L2C'
      ? 'This tailors the checklist and People chart for your property.'
      : 'Each candidate property below inherits this use (and can be overridden).';

  /** Label for the optional goal/aspiration textarea. */
  const goalLabel: string =
    tierKey === 'L1B'
      ? 'What outcome are you hoping for?'
      : tierKey === 'L1C' || tierKey === 'L2C'
      ? 'What does a successful plan review look like for you?'
      : 'What would a successful selection look like?';

  if (readOnly) {
    return (
      <Box title="Your goal & intended use" note="Saved">
        <ReadRow k="Intended use" v={useLabel(form.intended_use)} />
        <ReadRow k="Goal" v={(form.goals || []).filter(Boolean).join('; ')} />
      </Box>
    );
  }
  // Cards: the 9 intended uses.
  return (
    <Box title="Your goal & intended use" note={questionText}>
      <Field label="">
        <L1ALabel required>{questionText}</L1ALabel>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', margin: '0 0 8px' }}>
          {subNote}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 8,
          }}
        >
          {INTENDED_USES.map((use) => {
            const sel = form.intended_use === use;
            return (
              <button
                key={use}
                type="button"
                onClick={() => h.patch({ intended_use: use })}
                style={{
                  border: sel ? '1.5px solid var(--accent)' : '1.5px solid var(--rule-strong)',
                  background: sel ? 'var(--accent)' : 'var(--surface)',
                  color: sel ? '#fff' : 'var(--ink)',
                  borderRadius: 'var(--r-sm)',
                  padding: '10px 12px',
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {useLabel(use)}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 8 }}>
          {chipFooter}
        </div>
      </Field>
      <Field label={goalLabel} optional>
        <textarea
          style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }}
          value={form.goals[0] || ''}
          onChange={(e) => h.patch({ goals: e.target.value ? [e.target.value] : [] })}
          placeholder="Your overall goal for this project"
        />
      </Field>
    </Box>
  );
}

/* ───────────────── L1A · 3 — What's on your mind (two aspects) ───────────────── */

function L1AConcerns({
  form,
  readOnly,
  h,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
}) {
  // Concern palette is keyed by the INTENDED USE — routed through paletteFileFor
  // (office/commercial → commercial_office; hospitality/healthcare/mixed_use are
  // valid palette files). Falls back to residence when no use is chosen yet.
  const paletteKey = paletteFileFor(form.intended_use || 'residence');
  const [items, setItems] = useState<ConcernItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMergedConcerns(paletteKey)
      .then((list) => {
        if (!alive) return;
        setItems(list);
        const cats = orderedConcernCategories(list);
        setOpenCats((prev) => {
          if (Object.keys(prev).length) return prev;
          const open: Record<string, boolean> = {};
          cats.forEach((c, i) => {
            open[c] = i === 0;
          });
          return open;
        });
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [paletteKey]);

  const byCategory = useMemo(() => {
    const map = new Map<string, ConcernItem[]>();
    for (const it of items) {
      const arr = map.get(it.category) || [];
      arr.push(it);
      map.set(it.category, arr);
    }
    return map;
  }, [items]);
  const cats = useMemo(() => orderedConcernCategories(items), [items]);
  const knownIds = useMemo(() => new Set(items.map((it) => it.concern_id)), [items]);

  // The selected rows by concern_id (so we can read each row's issue/aspiration/note).
  const rowById = useMemo(() => {
    const m = new Map<string, ConcernInput>();
    for (const c of form.presenting_concerns) if (c.concern_id) m.set(c.concern_id, c);
    return m;
  }, [form.presenting_concerns]);

  // The free-text "Other" concern: the first row WITHOUT a concern_id.
  const otherEntry = form.presenting_concerns.find((c) => !c.concern_id);
  const otherText = otherEntry?.text || '';

  /** Rebuild the whole answer array from a desired map of selected rows + the
   *  free-text "Other". Checklist rows go in palette order; selections not in the
   *  current palette are carried so a use-switch never drops them. */
  function commit(nextById: Map<string, ConcernInput>, nextOther: string) {
    const rows: ConcernInput[] = items
      .filter((it) => nextById.has(it.concern_id))
      .map((it) => {
        const prev = nextById.get(it.concern_id)!;
        return { ...prev, text: it.label, concern_id: it.concern_id };
      });
    for (const [id, row] of nextById) {
      if (!knownIds.has(id)) rows.push(row); // carried selection (off-palette)
    }
    const other = nextOther.trim();
    if (other) rows.push({ text: other });
    h.patch({ presenting_concerns: rows });
  }

  function toggle(it: ConcernItem) {
    const next = new Map(rowById);
    if (next.has(it.concern_id)) next.delete(it.concern_id);
    else next.set(it.concern_id, { text: it.label, concern_id: it.concern_id, issue: false, aspiration: false });
    commit(next, otherText);
  }
  function setAspect(id: string, patch: Partial<ConcernInput>) {
    const next = new Map(rowById);
    const cur = next.get(id);
    if (!cur) return;
    next.set(id, { ...cur, ...patch });
    commit(next, otherText);
  }

  if (readOnly) {
    return (
      <Box title="What's on your mind?" note="Saved">
        {form.presenting_concerns.length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>No concerns selected.</p>
        )}
        {form.presenting_concerns.map((c, i) => {
          const aspects = [c.issue ? 'Issue' : '', c.aspiration ? 'Aspiration' : '']
            .filter(Boolean)
            .join(' + ');
          return <ReadRow key={i} k={c.text} v={[aspects, c.note].filter(Boolean).join(' — ')} />;
        })}
      </Box>
    );
  }

  return (
    <Box title="What's on your mind?" note="Tick an area, then mark issue and/or aspiration.">
      <div
        style={{
          fontSize: 12,
          color: 'var(--accent-deep, var(--ink))',
          background: 'var(--accent-wash)',
          borderLeft: '3px solid var(--accent)',
          borderRadius: '0 var(--r-sm) var(--r-sm) 0',
          padding: '8px 12px',
          margin: '0 0 10px',
          lineHeight: 1.5,
        }}
      >
        <b>Two aspects per area.</b> Tick an area, then mark whether it's a{' '}
        <b>problem you're facing</b>, an <b>aspiration</b> you hold, or <b>both</b> — add a line if
        you wish.
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 10px' }}>
        Checklist for {useLabel(form.intended_use || 'residence')} — switches with your intended use.
      </div>
      {loading ? (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cats.map((cat) => {
            const group = byCategory.get(cat) || [];
            if (!group.length) return null;
            const open = openCats[cat];
            return (
              <section key={cat} style={{ borderBottom: '1px solid var(--rule)', paddingBottom: 10 }}>
                <button
                  type="button"
                  onClick={() => setOpenCats((o) => ({ ...o, [cat]: !o[cat] }))}
                  aria-expanded={!!open}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'var(--ink-mute)',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  <span>{CONCERN_CATEGORY_TITLE[cat] ?? cat}</span>
                  <span style={{ fontSize: 14 }}>{open ? '–' : '+'}</span>
                </button>
                {open && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {group.map((it) => {
                      const row = rowById.get(it.concern_id);
                      const checked = !!row;
                      return (
                        <div
                          key={it.concern_id}
                          style={{
                            border: '1px solid var(--rule)',
                            borderRadius: 'var(--r-sm)',
                            padding: '8px 10px',
                          }}
                        >
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              fontSize: 13.5,
                              color: 'var(--ink)',
                              cursor: 'pointer',
                            }}
                          >
                            <input type="checkbox" checked={checked} onChange={() => toggle(it)} />
                            <span>{it.label}</span>
                          </label>
                          {checked && row && (
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                marginTop: 8,
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => setAspect(it.concern_id, { issue: !row.issue })}
                                style={chipStyle(!!row.issue, 'issue')}
                              >
                                ⚠ Facing an issue
                              </button>
                              <button
                                type="button"
                                onClick={() => setAspect(it.concern_id, { aspiration: !row.aspiration })}
                                style={chipStyle(!!row.aspiration, 'asp')}
                              >
                                ★ Aspiration
                              </button>
                              <input
                                style={{ ...fieldStyle, flex: '1 1 160px', minWidth: 140, width: 'auto' }}
                                value={row.note || ''}
                                onChange={(e) => setAspect(it.concern_id, { note: e.target.value })}
                                placeholder="Tell us more (optional) — kept word-for-word"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
          <Field label="Something else" optional hint="Anything not in the list above.">
            <input
              style={fieldStyle}
              value={otherText}
              onChange={(e) => commit(new Map(rowById), e.target.value)}
              placeholder="Anything else you'd like us to focus on"
            />
          </Field>
        </div>
      )}
    </Box>
  );
}

/** Chip style for the issue/aspiration toggles. */
function chipStyle(on: boolean, kind: 'issue' | 'asp'): React.CSSProperties {
  const base: React.CSSProperties = {
    border: '1.5px solid var(--rule-strong)',
    borderRadius: 999,
    padding: '4px 11px',
    fontSize: 11.5,
    fontWeight: 700,
    color: 'var(--ink-mute)',
    background: 'var(--surface)',
    cursor: 'pointer',
  };
  if (!on) return base;
  if (kind === 'issue')
    return { ...base, background: 'var(--danger-wash, #fee2e2)', borderColor: 'var(--danger, #ef4444)', color: 'var(--danger-deep, #991b1b)' };
  return { ...base, background: 'var(--ok-wash, #dcfce7)', borderColor: 'var(--ok, #22c55e)', color: 'var(--ok-deep, #166534)' };
}

/* ─────────────────── L1A · 4 — Property details (repeatable) ─────────────────── */

const L1A_MAX_PROPERTIES = 3;

function L1APropertyDetails({
  form,
  readOnly,
  h,
  /**
   * L1B (2026-06-23) — 'l1b' is the SINGLE-property occupied-space mode: no "+ Add
   * another property" (L1B always reviews one space you occupy), and each property
   * card shows the DRAWING GATE (do you have a floor plan?). Default 'l1a' keeps
   * the multi-property candidate-comparison behaviour unchanged.
   */
  mode = 'l1a',
  registry,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
  mode?: 'l1a' | 'l1b';
  /** AD-036 — staff-only registry context; enables the per-slot link control. */
  registry?: PropertyRegistryCtx;
}) {
  const single = mode === 'l1b';
  const properties = form.properties.length
    ? form.properties
    : [blankProperty(form.intended_use)];

  // AD-036 — one shared registry listener for all slots (avoids N listeners).
  // Only subscribes when staff registry context is present.
  const [allProps, setAllProps] = useState<LiveProperty[]>([]);
  useEffect(() => {
    if (!registry) return;
    const unsub = listenAllProperties(setAllProps);
    return () => unsub();
  }, [registry]);

  // AD-036 — per-slot "staff is entering a new property" flags (slot id → bool).
  // Lifted here so we can both drive PropertySlotLink AND gate the capture body
  // below it: a slot's capture fields show only when LINKED or entering-new.
  const [enteringNew, setEnteringNew] = useState<Record<string, boolean>>({});
  const setSlotEnteringNew = (slotId: string, v: boolean) =>
    setEnteringNew((m) => ({ ...m, [slotId]: v }));

  function setProp(i: number, patch: Partial<PropertyInput>) {
    const next = (form.properties.length ? form.properties : properties).slice();
    next[i] = { ...next[i], ...patch };
    h.patch({ properties: next });
  }
  function addProp() {
    if (form.properties.length >= L1A_MAX_PROPERTIES) return;
    h.patch({ properties: [...form.properties, blankProperty(form.intended_use)] });
  }
  function removeProp(i: number) {
    h.patch({ properties: form.properties.filter((_, j) => j !== i) });
  }

  if (readOnly) {
    return (
      <Box title="Property details" note="Saved">
        {properties.filter((p) => p.label || p.use || p.coordPair || p.builtUpAreaSqft).length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>No properties added.</p>
        )}
        {(single ? properties.slice(0, 1) : properties).map((p, i) => (
          <div key={p.id} style={{ marginBottom: 8 }}>
            <ReadRow k={single ? 'Property' : `Property ${i + 1}`} v={[p.label, useLabel(p.use), p.builtUpAreaSqft ? `${p.builtUpAreaSqft} sq ft` : ''].filter(Boolean).join(' · ')} />
            {single && p.hasDrawing && (
              <ReadRow k=" · drawing" v={p.hasDrawing === 'yes' ? 'Has a floor plan' : 'No plan — survey / sketch needed'} />
            )}
            {(p.floors || []).filter((f) => f.floor).map((f) => (
              <ReadRow key={f.id} k=" · floor" v={[f.floor, f.areaApprox ? `${f.areaApprox} sq ft` : ''].filter(Boolean).join(' · ')} />
            ))}
            {(p.media || []).length > 0 && <ReadRow k=" · media" v={`${p.media.length} item(s)`} />}
          </div>
        ))}
      </Box>
    );
  }

  // L1B = a single occupied property; only ever render the first card.
  const cards = single ? properties.slice(0, 1) : properties;
  return (
    <Box
      title="Property details"
      note={
        registry
          ? 'Saved to Manage Properties automatically'
          : single
            ? 'The space you occupy and want reviewed.'
            : 'The candidate properties you are comparing.'
      }
      allowOverflow
    >
      {registry && (
        <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 12px' }}>
          Add one or more properties. Link an existing one by its number (P#####) or address, or
          enter a new one — either way it's registered in Manage Properties under one running
          sequence.
        </p>
      )}
      {cards.map((p, i) => (
        <PropertyCard
          key={p.id}
          p={p}
          index={i}
          canRemove={!single && form.properties.length > 1}
          drawingGate={single}
          hideLabel={single}
          onChange={(patch) => setProp(i, patch)}
          onRemove={() => removeProp(i)}
          // Capture body shows when there's no registry (client/legacy path) OR
          // the slot is linked (write-through) OR staff is entering a new one.
          captureVisible={!registry || !!p.propertyId?.trim() || !!enteringNew[p.id]}
          slotLink={
            registry ? (
              <PropertySlotLink
                prop={p}
                allProps={allProps}
                scope={registry.scope}
                tier={registry.tier}
                actorUid={registry.actorUid}
                isPrimary={i === 0}
                onPatchSlot={(patch) => setProp(i, patch)}
                onMsg={registry.onMsg}
                radiusM={registry.radiusM}
                enteringNew={!!enteringNew[p.id]}
                onEnteringNewChange={(v) => setSlotEnteringNew(p.id, v)}
              />
            ) : null
          }
        />
      ))}
      {!single && form.properties.length < L1A_MAX_PROPERTIES && (
        <Btn3 intent="ink" size="sm" onClick={addProp}>
          {registry ? '＋ Add another property' : '+ Add another property (candidate)'}
        </Btn3>
      )}
      {/* AD-036 — one sequence / one list reassurance (registry path only). */}
      {registry && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--accent-deep, #3a536b)',
            background: 'var(--accent-wash, #eef3f8)',
            border: '1px solid var(--accent-line, #d3e0ec)',
            borderRadius: 'var(--r-sm)',
            padding: '11px 14px',
            marginTop: 14,
          }}
        >
          ℹ <b>One sequence, one list.</b> Every property — created from the Properties page, Leads,
          Forms, or a Property detail — shares the same running number (P00001, P00002, P00003 …)
          and shows together in Manage Properties. Linking here never makes a duplicate.
        </div>
      )}
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--warn-deep, #7a4f08)',
          background: 'var(--warn-wash, #fffbeb)',
          borderLeft: '3px solid var(--warn, #f59e0b)',
          borderRadius: '0 var(--r-sm) var(--r-sm) 0',
          padding: '8px 12px',
          marginTop: 12,
        }}
      >
        <b>Not in this form (evaluation stage):</b> boundary tracing, grid, entrance marking,
        fixtures. Our team builds those from your floor plans.
      </div>
    </Box>
  );
}

function PropertyCard({
  p,
  index,
  canRemove,
  drawingGate = false,
  hideLabel = false,
  onChange,
  onRemove,
  slotLink = null,
  captureVisible = true,
}: {
  p: PropertyInput;
  index: number;
  canRemove: boolean;
  /** L1B — show the "Do you have a floor plan?" gate above Floors & area. */
  drawingGate?: boolean;
  /** L1B — hide the per-candidate "Property name / label" field (single space). */
  hideLabel?: boolean;
  onChange: (patch: Partial<PropertyInput>) => void;
  onRemove: () => void;
  /** AD-036 — staff-only inline link-or-new control, rendered at the slot top. */
  slotLink?: React.ReactNode;
  /** AD-036 — when false (registry, slot not linked/not entering-new) the
   * heavy capture body is hidden; only the slot header + link control show. */
  captureVisible?: boolean;
}) {
  // Floor options for the media tag = the floors added on this property + the
  // canonical labels (so the client can also pick a standard floor).
  const propFloorOptions = useMemo(() => {
    const added = (p.floors || []).map((f) => (f.floor || '').trim()).filter(Boolean);
    return Array.from(new Set([...added, ...FLOOR_OPTIONS]));
  }, [p.floors]);

  // Fix #1: show ONE blank floor row by default (never a section that opens
  // with only an Add button). When the array is empty we render a single
  // synthetic blank row; the first edit materialises it into p.floors.
  const displayFloors = p.floors.length ? p.floors : [blankFloor()];

  // Keep a live ref of the current media so async thumbnail callbacks patch the
  // latest array (not a stale closure) when they resolve.
  const mediaRef = useRef(p.media);
  mediaRef.current = p.media;

  function setFloor(i: number, patch: Partial<FloorInput>) {
    const base = p.floors.length ? p.floors : displayFloors;
    const next = base.slice();
    next[i] = { ...next[i], ...patch };
    onChange({ floors: next });
  }
  function addFloor() {
    const base = p.floors.length ? p.floors : displayFloors;
    onChange({ floors: [...base, blankFloor()] });
  }
  function removeFloor(i: number) {
    const base = p.floors.length ? p.floors : displayFloors;
    onChange({ floors: base.filter((_, j) => j !== i) });
  }
  // Attach (or replace) a floor plan — captures the file's NAME + MIME so the UI
  // can show WHAT was uploaded (thumbnail for images, 📄 + filename for PDFs)
  // instead of a bare tick. Same handler powers both "⬆ Plan" and "Replace".
  function attachPlan(
    i: number,
    file: File | null | undefined,
    e?: React.ChangeEvent<HTMLInputElement>,
  ) {
    if (file) {
      const isPdf =
        file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      setFloor(i, {
        planUrl: URL.createObjectURL(file),
        planName: file.name,
        planMime: file.type || (isPdf ? 'application/pdf' : 'image/*'),
      });
    }
    if (e) e.target.value = '';
  }
  function setMedia(i: number, patch: Partial<MediaInput>) {
    const next = p.media.slice();
    next[i] = { ...next[i], ...patch };
    onChange({ media: next });
  }
  // Patch a media row by id (so async thumbnail writes survive intervening edits).
  function patchMediaById(id: string, patch: Partial<MediaInput>) {
    onChange({ media: mediaRef.current.map((m) => (m.id === id ? { ...m, ...patch } : m)) });
  }
  function removeMedia(i: number) {
    onChange({ media: p.media.filter((_, j) => j !== i) });
  }
  // Fix #3: Upload (gallery, multi) and Capture live (capture-attribute camera
  // input) both land here — mirrors the Quick Capture app's handleFileCapture:
  // each File becomes a row, then a thumbnail is generated (image snapshot /
  // mid-clip video frame) and patched onto the row by id (non-blocking).
  function addMediaFiles(files: FileList | null) {
    if (!files || !files.length) return;
    const rows: MediaInput[] = Array.from(files).map((f) => ({
      id: newRowId('media'),
      url: URL.createObjectURL(f),
      type: mediaKindOf(f),
      mimeType: f.type || defaultMime(mediaKindOf(f)),
      floor: '',
      place: '',
    }));
    onChange({ media: [...p.media, ...rows] });
    // Generate thumbnails after the rows are in (capture-app pattern).
    Array.from(files).forEach((f, idx) => {
      const rowId = rows[idx].id;
      void generateThumbnail(f).then((thumb) => {
        if (thumb) patchMediaById(rowId, { thumbUrl: thumb });
      });
    });
  }

  return (
    <div
      style={{
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-sm)',
        padding: 14,
        marginBottom: 12,
        background: 'var(--surface)',
      }}
    >
      {/* Slot header — shown for multi-property OR whenever the registry slot
          link is present (so even single-property L1B gets "Property 1" + the
          inline link control above the capture fields). */}
      {(!hideLabel || slotLink) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            Property {index + 1}
          </span>
          {canRemove && (
            <Btn3 intent="ghost" size="sm" onClick={onRemove} aria-label="Remove property">
              {slotLink ? '✕ Remove' : 'Remove'}
            </Btn3>
          )}
        </div>
      )}

      {/* AD-036 — inline link-or-new control at the top of the slot. */}
      {slotLink}

      {captureVisible && !hideLabel && (
        <Field label="Property name / label" optional>
          <input
            style={fieldStyle}
            value={p.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="e.g. 'Plot near Ring Road'"
          />
        </Field>
      )}

      {captureVisible && (
      <>
      <Field label="Use of this space" hint="Inherits your intended use; change if this candidate differs.">
        <select
          style={fieldStyle}
          value={p.use}
          onChange={(e) => onChange({ use: e.target.value as ProjectType | '' })}
        >
          <option value="">Select…</option>
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {useLabel(t)}
            </option>
          ))}
        </select>
      </Field>

      {/* Site location & north — the shared SiteLocationCard (Maps link OR
          coordinates, Open-in-Maps/Earth, True-north bearing, Earth screenshot).
          Same component the L1C/L2C drawings block uses. */}
      <SiteLocationCard v={p} onChange={(patch) => onChange(patch)} />

      {/* Floors & area */}
      <div
        style={{
          border: '1px solid var(--rule)',
          borderRadius: 'var(--r-sm)',
          padding: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>Floors &amp; area</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Approx built-up area (sq ft)" hint="Drives the quote (per-sq ft tiers) &amp; the grid.">
            <input
              style={fieldStyle}
              value={p.builtUpAreaSqft || ''}
              onChange={(e) => onChange({ builtUpAreaSqft: e.target.value })}
              placeholder="e.g. 3204"
              inputMode="numeric"
            />
          </Field>
          <Field label="Plot / land area (sq ft)" optional>
            <input
              style={fieldStyle}
              value={p.plotAreaSqft || ''}
              onChange={(e) => onChange({ plotAreaSqft: e.target.value })}
              placeholder="e.g. 5000"
              inputMode="numeric"
            />
          </Field>
        </div>

        {/* L1B DRAWING GATE — a measured plan is what lets our team trace the
            boundary & build the grid; photos alone can't. Yes → per-floor plan
            upload; No → the Survey & Map-Making add-on / measured-sketch path. */}
        {drawingGate && (
          <div style={{ margin: '4px 0 10px' }}>
            <label style={labelStyle}>
              Do you have a floor plan / drawing of this space?{reqMark}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, maxWidth: 420, marginTop: 6 }}>
              {([
                ['yes', "Yes — I'll upload it"],
                ['no', "No — I don't have one"],
              ] as const).map(([val, lbl]) => {
                const sel = p.hasDrawing === val;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => onChange({ hasDrawing: val })}
                    style={{
                      border: sel ? '1.5px solid var(--accent)' : '1.5px solid var(--rule-strong)',
                      background: sel ? 'var(--accent)' : 'var(--surface)',
                      color: sel ? '#fff' : 'var(--ink)',
                      borderRadius: 'var(--r-sm)',
                      padding: '10px 12px',
                      fontSize: 12.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {drawingGate && p.hasDrawing === 'no' && (
          <div style={{ margin: '4px 0 10px' }}>
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--danger-deep, #991b1b)',
                background: 'var(--danger-wash, #fef2f2)',
                borderLeft: '3px solid var(--danger, #dc2626)',
                borderRadius: '0 var(--r-sm) var(--r-sm) 0',
                padding: '8px 12px',
              }}
            >
              <b>We need a plan to grid your space.</b> Photos and videos show us the rooms, but we
              can't accurately trace the boundary and map the 16/32 zones without a measured floor
              plan. Two ways forward: upload a hand sketch with wall measurements, or request our{' '}
              <b>Survey &amp; Map-Making</b> add-on (quoted separately).
            </div>
          </div>
        )}

        {/* Per-floor plan upload — shown for L1A always, and for L1B only when the
            client said they HAVE a drawing (gate = 'yes' / unset). */}
        {(!drawingGate || p.hasDrawing !== 'no') && (
        <>
        <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink)', margin: '6px 0' }}>
          Floors in this property — add each floor, its area &amp; its plan
        </div>
        {displayFloors.map((f, fi) => (
          <div
            key={f.id}
            style={{ display: 'grid', gridTemplateColumns: '1.3fr .8fr auto auto', gap: 8, alignItems: 'center', marginBottom: 8 }}
          >
            <Combobox
              value={f.floor}
              onChange={(v) => setFloor(fi, { floor: v })}
              options={FLOOR_OPTIONS}
              allowAdd
              placeholder="Floor (e.g. Ground Floor)"
              ariaLabel="Floor"
            />
            <input
              style={fieldStyle}
              value={f.areaApprox || ''}
              onChange={(e) => setFloor(fi, { areaApprox: e.target.value })}
              placeholder="Area sq ft"
              inputMode="numeric"
            />
            {f.planUrl ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                {isPdfPlan(f) ? (
                  <span style={planThumbStyle} title={f.planName || 'Plan (PDF)'} aria-label="PDF plan">📄</span>
                ) : (
                  <img src={f.planUrl} alt={f.planName || 'Floor plan'} style={planThumbStyle} />
                )}
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span
                    style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={f.planName || 'Plan attached'}
                  >
                    {f.planName || 'Plan attached'}
                  </span>
                  <span style={{ display: 'flex', gap: 8 }}>
                    <label style={planLinkStyle}>
                      Replace
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => attachPlan(fi, e.target.files?.[0], e)}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setFloor(fi, { planUrl: undefined, planStoragePath: undefined, planName: undefined, planMime: undefined })}
                      style={{ ...planLinkStyle, color: 'var(--danger, #b3261e)' }}
                      aria-label="Remove plan"
                    >
                      Remove
                    </button>
                  </span>
                </div>
              </div>
            ) : (
              <label style={{ ...miniBtnStyle, textAlign: 'center' }}>
                ⬆ Plan
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => attachPlan(fi, e.target.files?.[0], e)}
                />
              </label>
            )}
            <Btn3 intent="ghost" size="sm" onClick={() => removeFloor(fi)} aria-label="Remove floor">
              ✕
            </Btn3>
          </div>
        ))}
        <Btn3 intent="ghost" size="sm" onClick={addFloor}>
          + Add another floor
        </Btn3>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>
          Our team traces the boundary &amp; builds the grid from these plans — you don't draw
          anything. The floors you add here also fill the photo/video Floor tags below.
        </div>
        </>
        )}
      </div>

      {/* Photos & videos — mirrors the Quick Capture app's Camera/Gallery
          pattern: Upload = a normal multi-file picker (gallery / folder);
          Capture live = a `capture`-attribute file input that opens the native
          device camera. NO from-scratch getUserMedia modal. */}
      <Field label="Photos & videos">
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          {/* Upload (Gallery) = multi-file picker, no capture attr (one OR many). */}
          <label style={{ ...miniBtnStyle, flex: 1, textAlign: 'center', padding: '11px' }}>
            ⬆ Upload (gallery — pick one or many)
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                addMediaFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
          {/* Capture live (Camera) = `capture`-attribute input → native camera
              on mobile (same as capture-app input-photo-camera / -video-camera). */}
          <label style={{ ...miniBtnStyle, flex: 1, textAlign: 'center', padding: '11px', background: 'var(--ok-deep, #0f6f4e)', color: '#fff', borderColor: 'var(--ok-deep, #0f6f4e)' }}>
            📷 Capture live
            <input
              type="file"
              accept="image/*,video/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => {
                addMediaFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
        </div>
        {p.media.map((m, mi) => (
          <div
            key={m.id}
            style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10, border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 8 }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 'var(--r-sm)',
                background: 'var(--rule)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                fontSize: 24,
              }}
            >
              {m.thumbUrl ? (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <img src={m.thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {m.type === 'video' && (
                    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, textShadow: '0 1px 3px rgba(0,0,0,.6)' }}>▶</span>
                  )}
                </div>
              ) : m.type === 'video' ? '🎬' : m.url ? (
                <img src={m.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : '🖼'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, color: 'var(--ink-mute)', fontWeight: 600 }}>Floor</div>
              <Combobox
                value={m.floor}
                onChange={(v) => setMedia(mi, { floor: v })}
                options={propFloorOptions}
                allowAdd
                placeholder="e.g. Ground floor"
                ariaLabel="Media floor tag"
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, color: 'var(--ink-mute)', fontWeight: 600 }}>Place / room</div>
              <Combobox
                value={m.place}
                onChange={(v) => setMedia(mi, { place: v })}
                options={PLACE_OPTIONS}
                allowAdd
                placeholder="e.g. Kitchen"
                ariaLabel="Media place tag"
              />
            </div>
            <Btn3 intent="ghost" size="sm" onClick={() => removeMedia(mi)} aria-label="Remove media">
              remove
            </Btn3>
          </div>
        ))}
        <div style={{ fontSize: 11, color: 'var(--ink-mute)' }}>
          Each item is tagged by <b>Floor + Place/room</b> (type to pick or add) — so our team
          filters photos/videos floor-wise and room-wise to write the report quickly.
        </div>
      </Field>
      </>
      )}
    </div>
  );
}

const miniBtnStyle: React.CSSProperties = {
  border: '1px solid var(--accent)',
  color: 'var(--accent)',
  background: 'var(--surface)',
  borderRadius: 'var(--r-sm)',
  padding: '7px 12px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-block',
};

/** Is this floor's attached plan a PDF (vs an image)? Drives the icon choice. */
export function isPdfPlan(f: { planMime?: string; planName?: string }): boolean {
  return (
    f.planMime === 'application/pdf' ||
    (f.planName || '').toLowerCase().endsWith('.pdf')
  );
}

/** 40×40 plan thumbnail / PDF-icon cell. */
const planThumbStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--rule)',
  background: 'var(--rule)',
  objectFit: 'cover',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
};

/** Small text-link affordance ("Replace" / "Remove") under a plan name. */
const planLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--accent)',
};

/** Open the coords in Google Maps / Earth (only when the coordPair parses). */
function PropertyMapButton({ coordPair, kind }: { coordPair: string; kind: 'maps' | 'earth' }) {
  const pair = parseCoordPair(coordPair);
  const disabled = !pair;
  const href = pair ? (kind === 'maps' ? googleMapsUrl(pair.lat, pair.lon) : googleEarthUrl(pair.lat, pair.lon)) : '#';
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => disabled && e.preventDefault()}
      style={{
        ...miniBtnStyle,
        opacity: disabled ? 0.45 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        textDecoration: 'none',
      }}
    >
      {kind === 'maps' ? 'Open in Maps' : 'Open in Google Earth'}
    </a>
  );
}

/* ───────────── Shared: Site location & north card (L1A property + L1C/L2C drawings) ───────────── */

/** The fields the site-location card reads/writes — a structural subset shared by
 *  PropertyInput and DrawingsInput (same field names), so ONE component serves both. */
interface SiteLocationShape {
  google_maps_url: string;
  coordPair: string;
  northRotation?: string;
  northScreenshotUrl?: string;
}

/**
 * The FULL "Site location & north" card — Google Maps link OR coordinates (one
 * fills the other), Open-in-Maps / Open-in-Earth, True-north bearing (°), Google
 * Earth screenshot. Extracted from the L1A PropertyCard so L1C/L2C drawings reuse
 * the IDENTICAL component instead of a trimmed hand-rolled variant (the owner's
 * fix #1: the True-north bearing field must be present on the drawings block).
 */
function SiteLocationCard({
  v,
  onChange,
  blurb,
}: {
  v: SiteLocationShape;
  onChange: (patch: Partial<SiteLocationShape>) => void;
  blurb?: string;
}) {
  return (
    <div
      style={{
        background: 'var(--accent-wash)',
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-sm)',
        padding: 12,
        margin: '6px 0 12px',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>
        📍 Site location &amp; north
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>
        {blurb || 'Provide at least one of link / coordinates (required) — either one fills the other.'}
      </div>
      <Field label="Google Maps link *" hint="…or coordinates below; either one fills the other. At least one is required.">
        <input
          style={fieldStyle}
          value={v.google_maps_url}
          onChange={(e) => {
            const url = e.target.value;
            const parsed = parseGoogleMapsUrl(url);
            if (parsed) {
              onChange({ google_maps_url: url, coordPair: formatCoordPair(parsed.lat, parsed.lon) });
            } else {
              onChange({ google_maps_url: url });
            }
          }}
          placeholder="Paste the full maps.google.com link"
        />
      </Field>
      <Field label="Coordinates (latitude, longitude) *" hint="Enter either — the other fills automatically. At least one is required.">
        <input
          style={fieldStyle}
          value={v.coordPair}
          onChange={(e) => onChange({ coordPair: e.target.value })}
          placeholder="22.7196, 75.8577"
        />
      </Field>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        <PropertyMapButton coordPair={v.coordPair} kind="maps" />
        <PropertyMapButton coordPair={v.coordPair} kind="earth" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        <Field label="True-north bearing (°)" optional>
          <input
            style={fieldStyle}
            value={v.northRotation || ''}
            onChange={(e) => onChange({ northRotation: e.target.value })}
            placeholder="e.g. 347"
            inputMode="decimal"
          />
        </Field>
        <Field label="Google Earth screenshot" optional>
          <input
            type="file"
            accept="image/*"
            style={{ fontSize: 12 }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onChange({ northScreenshotUrl: URL.createObjectURL(f) });
            }}
          />
        </Field>
      </div>
    </div>
  );
}

/* ───────────── Shared: per-floor plan-upload rows (L1A property + L1C/L2C versions) ───────────── */

/** Attach a plan file to a floor — captures name + mime for the thumbnail/label. */
function planPatchFromFile(file: File): Partial<FloorInput> {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  return {
    planUrl: URL.createObjectURL(file),
    planName: file.name,
    planMime: file.type || (isPdf ? 'application/pdf' : 'image/*'),
  };
}

/**
 * The per-floor "Floor · Area · Plan upload" rows — extracted so L1C/L2C drawing
 * versions reuse the SAME row UI the L1A/L1B property floors use. `uploadLabel`
 * lets a version call the upload "Drawing" (vs the property's "Plan").
 */
function FloorPlanRows({
  floors,
  setFloor,
  addFloor,
  removeFloor,
  uploadLabel = 'Plan',
}: {
  floors: FloorInput[];
  setFloor: (i: number, patch: Partial<FloorInput>) => void;
  addFloor: () => void;
  removeFloor: (i: number) => void;
  uploadLabel?: string;
}) {
  const rows = floors.length ? floors : [blankFloor()];
  return (
    <>
      {rows.map((f, fi) => (
        <div
          key={f.id}
          style={{ display: 'grid', gridTemplateColumns: '1.3fr .8fr auto auto', gap: 8, alignItems: 'center', marginBottom: 8 }}
        >
          <Combobox
            value={f.floor}
            onChange={(val) => setFloor(fi, { floor: val })}
            options={FLOOR_OPTIONS}
            allowAdd
            placeholder="Floor (e.g. Ground Floor)"
            ariaLabel="Floor"
          />
          <input
            style={fieldStyle}
            value={f.areaApprox || ''}
            onChange={(e) => setFloor(fi, { areaApprox: e.target.value })}
            placeholder="Area sq ft"
            inputMode="numeric"
          />
          {f.planUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              {isPdfPlan(f) ? (
                <span style={planThumbStyle} title={f.planName || 'Drawing (PDF)'} aria-label="PDF drawing">📄</span>
              ) : (
                <img src={f.planUrl} alt={f.planName || 'Drawing'} style={planThumbStyle} />
              )}
              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <span
                  style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={f.planName || 'Attached'}
                >
                  {f.planName || 'Attached'}
                </span>
                <span style={{ display: 'flex', gap: 8 }}>
                  <label style={planLinkStyle}>
                    Replace
                    <input
                      type="file"
                      accept="application/pdf,image/*,.dwg,.dxf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setFloor(fi, planPatchFromFile(file));
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setFloor(fi, { planUrl: undefined, planStoragePath: undefined, planName: undefined, planMime: undefined })}
                    style={{ ...planLinkStyle, color: 'var(--danger, #b3261e)' }}
                    aria-label="Remove drawing"
                  >
                    Remove
                  </button>
                </span>
              </div>
            </div>
          ) : (
            <label style={{ ...miniBtnStyle, textAlign: 'center' }}>
              ⬆ {uploadLabel}
              <input
                type="file"
                accept="application/pdf,image/*,.dwg,.dxf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setFloor(fi, planPatchFromFile(file));
                  e.target.value = '';
                }}
              />
            </label>
          )}
          <Btn3 intent="ghost" size="sm" onClick={() => removeFloor(fi)} aria-label="Remove floor">
            ✕
          </Btn3>
        </div>
      ))}
      <Btn3 intent="ghost" size="sm" onClick={addFloor}>
        + Add another floor
      </Btn3>
    </>
  );
}

/* ───────────── L1C / L2C · drawings_upload (REAL, v2 2026-06-23) ───────────── */

/**
 * The drawings-upload module. PROPERTY block (full site-location + north + area,
 * common to every version) + drawing VERSIONS. L1C = single version (no "add
 * version"); L2C = repeatable versions over time + architect-contact block. The
 * architect block is hidden on L1C via the bright line (hidesArchitectContact).
 */
function DrawingsUploadModule({
  form,
  readOnly,
  h,
  serviceTier,
  registry,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
  serviceTier?: string;
  /**
   * AD-036.2 — staff-only registry context. When present, the SAME unified
   * link-or-new control (PropertySlotLink) renders at the top of the property
   * block on the drawings stage, so L1C/L2C link/register the property exactly
   * like L1A. The drawings-versioning behaviour per tier is unchanged.
   */
  registry?: PropertyRegistryCtx;
}) {
  const d = form.drawings || blankDrawings();
  const multiVersion = !singleDrawingSet(serviceTier || ''); // L2C = multi (revisions); L1C/L2A = single set
  const showArchitect = !hidesArchitectContact(serviceTier || ''); // coordination tier (L2C/L2A) → pointer; L1C → independent-review note
  // L1C is single-set → render exactly one version; L2C → all versions.
  const versions = d.versions.length ? d.versions : [blankDrawingVersion()];

  // AD-036.2 — one shared registry listener (only when staff context present),
  // mirrors L1APropertyDetails. The drawings property block is a SINGLE slot, so
  // there is exactly one PropertySlotLink (always the primary).
  const [allProps, setAllProps] = useState<LiveProperty[]>([]);
  useEffect(() => {
    if (!registry) return;
    const unsub = listenAllProperties(setAllProps);
    return () => unsub();
  }, [registry]);
  // "Staff is entering a new property" — gates the property capture body below
  // the link control (same pattern as the L1A slot).
  const [enteringNew, setEnteringNew] = useState(false);
  const linked = !!d.propertyId?.trim();
  // The property capture block (site location + area) shows on the legacy/client
  // path always; on the registry path only when linked (write-through) or when
  // staff are entering a new property. The drawing VERSIONS always show.
  const propertyCaptureVisible = !registry || linked || enteringNew;

  function patchD(patch: Partial<DrawingsInput>) {
    h.patch({ drawings: { ...d, ...patch } });
  }
  // AD-036.2 — present the drawings property block as a synthetic PropertyInput so
  // the unified PropertySlotLink + propertyInputToSeed work without duplication;
  // patches (e.g. { propertyId }) are mapped back onto the drawings block.
  const slotProp = drawingsToPropertyInput(d, {
    address: form.property_address,
    use: form.project_type,
  });
  function patchSlot(patch: Partial<PropertyInput>) {
    patchD(applyPropertyPatchToDrawings(patch));
  }
  function setVersion(i: number, patch: Partial<DrawingVersion>) {
    const base = d.versions.length ? d.versions : versions;
    const next = base.slice();
    next[i] = { ...next[i], ...patch };
    patchD({ versions: next });
  }
  function addVersion() {
    const base = d.versions.length ? d.versions : versions;
    patchD({ versions: [...base, blankDrawingVersion()] });
  }
  function removeVersion(i: number) {
    const base = d.versions.length ? d.versions : versions;
    patchD({ versions: base.filter((_, j) => j !== i) });
  }
  function setFloorOn(vi: number, fiBase: FloorInput[], fi: number, patch: Partial<FloorInput>) {
    const next = (fiBase.length ? fiBase : [blankFloor()]).slice();
    next[fi] = { ...next[fi], ...patch };
    setVersion(vi, { floors: next });
  }

  if (readOnly) {
    return (
      <Box title="The property & architect drawings" note="Saved">
        <ReadRow k="Site" v={[d.coordPair, d.google_maps_url].filter(Boolean).join(' · ') || '—'} />
        {(d.northRotation || '').trim() && <ReadRow k="True-north (°)" v={d.northRotation!} />}
        {(versions || []).map((v, i) => (
          <ReadRow
            key={v.id}
            k={multiVersion ? `Version ${i + 1}` : 'Drawings'}
            v={[
              v.stage ? DRAWING_STAGES.find((s) => s.value === v.stage)?.label || v.stage : '',
              v.label,
              `${(v.floors || []).filter((f) => f.planUrl).length} drawing(s)`,
            ].filter(Boolean).join(' · ')}
          />
        ))}
        {(d.surveyStatus || '').trim() && <ReadRow k="Measured survey" v={d.surveyStatus!} />}
        {(d.existingDrawings || '').trim() && <ReadRow k="Existing drawings" v={d.existingDrawings!} />}
        {(d.siteConstraints || '').trim() && <ReadRow k="Site constraints" v={d.siteConstraints!} />}
        {(d.mediaNote || '').trim() && <ReadRow k="Photos & video" v={d.mediaNote!} />}
      </Box>
    );
  }

  return (
    <Box
      title="The property & your architect's drawings"
      note={
        registry
          ? 'Saved to Manage Properties automatically · Site location & north (shared) + the drawing set.'
          : 'Site location & north (shared) + the drawing set.'
      }
      allowOverflow
    >
      {/* PROPERTY — common to every drawing / version (the FULL L1A site-location block). */}
      <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 12, marginBottom: 12, background: 'var(--surface)' }}>
        {/* AD-036.2 — the SAME unified link-or-new control as the L1A property step,
            at the TOP of the drawings-stage property block (staff registry only). */}
        {registry && (
          <PropertySlotLink
            prop={slotProp}
            allProps={allProps}
            scope={registry.scope}
            tier={registry.tier}
            actorUid={registry.actorUid}
            isPrimary
            onPatchSlot={patchSlot}
            onMsg={registry.onMsg}
            radiusM={registry.radiusM}
            enteringNew={enteringNew}
            onEnteringNewChange={setEnteringNew}
          />
        )}
        {propertyCaptureVisible && (
          <>
            <SiteLocationCard
              v={d}
              onChange={(patch) => patchD(patch)}
              blurb="The property — common to every drawing & version below. Provide at least one of link / coordinates."
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Built-up area (sq ft)" optional>
                <input
                  style={fieldStyle}
                  value={d.builtUpAreaSqft || ''}
                  onChange={(e) => patchD({ builtUpAreaSqft: e.target.value })}
                  placeholder="e.g. 3204"
                  inputMode="numeric"
                />
              </Field>
              <Field label="Plot / land area (sq ft)" optional>
                <input
                  style={fieldStyle}
                  value={d.plotAreaSqft || ''}
                  onChange={(e) => patchD({ plotAreaSqft: e.target.value })}
                  placeholder="e.g. 5000"
                  inputMode="numeric"
                />
              </Field>
            </div>
          </>
        )}
      </div>
      {/* AD-036.2 — one sequence / one list reassurance (registry path only). */}
      {registry && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--accent-deep, #3a536b)',
            background: 'var(--accent-wash, #eef3f8)',
            border: '1px solid var(--accent-line, #d3e0ec)',
            borderRadius: 'var(--r-sm)',
            padding: '11px 14px',
            marginBottom: 12,
          }}
        >
          ℹ <b>One sequence, one list.</b> Link this drawing set to an existing property by its
          number (P#####) or address, or enter a new one — either way it shares the same running
          sequence and shows in Manage Properties. Linking never makes a duplicate.
        </div>
      )}

      {/* ② ABOUT THE BUILDING — folded in (2026-06-24 full merge). These four
          fields were the separate property_media / space_survey / property_drawings /
          property_constraints stub modules. They describe the same building, so
          they live here with the property, not on their own tab. */}
      {propertyCaptureVisible && (
        <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 12, marginBottom: 12, background: 'var(--surface)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>
            About the building
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Photos &amp; video" optional>
              <input
                style={fieldStyle}
                value={d.mediaNote || ''}
                onChange={(e) => patchD({ mediaNote: e.target.value })}
                placeholder="Add in the media panel · note here"
              />
            </Field>
            <Field label="Measured survey?" optional>
              <select
                style={fieldStyle}
                value={d.surveyStatus || ''}
                onChange={(e) => patchD({ surveyStatus: e.target.value })}
              >
                <option value="">Select…</option>
                <option value="Yes — I can share it">Yes — I can share it</option>
                <option value="No — please arrange one">No — please arrange one</option>
                <option value="Not sure">Not sure</option>
              </select>
            </Field>
            <Field label="Existing drawings you already have" optional>
              <textarea
                style={{ ...fieldStyle, minHeight: 48, resize: 'vertical' }}
                rows={2}
                value={d.existingDrawings || ''}
                onChange={(e) => patchD({ existingDrawings: e.target.value })}
                placeholder="e.g. floor plans, elevations, structural, MEP"
              />
            </Field>
            <Field label="Known site constraints" optional>
              <textarea
                style={{ ...fieldStyle, minHeight: 48, resize: 'vertical' }}
                rows={2}
                value={d.siteConstraints || ''}
                onChange={(e) => patchD({ siteConstraints: e.target.value })}
                placeholder="boundaries, setbacks, slopes, anything fixed"
              />
            </Field>
          </div>
        </div>
      )}

      {/* DRAWING VERSION(S). */}
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
        {multiVersion
          ? "Your architect's drawings — add each version as it comes (same property above)"
          : "Your architect's drawing set"}
      </div>
      {(multiVersion ? versions : versions.slice(0, 1)).map((v, vi) => (
        <div key={v.id} style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 12, marginBottom: 10, background: 'var(--surface)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
              {multiVersion ? `Drawing version ${vi + 1}` : 'Drawings'}
            </span>
            {multiVersion && versions.length > 1 && (
              <Btn3 intent="ghost" size="sm" onClick={() => removeVersion(vi)} aria-label="Remove version">
                Remove
              </Btn3>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Drawing stage">
              <select
                style={fieldStyle}
                value={v.stage}
                onChange={(e) => setVersion(vi, { stage: e.target.value as DrawingStage | '' })}
              >
                <option value="">Select…</option>
                {DRAWING_STAGES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Version label / note" optional>
              <input
                style={fieldStyle}
                value={v.label}
                onChange={(e) => setVersion(vi, { label: e.target.value })}
                placeholder="e.g. Concept v1 · revised after NE change"
              />
            </Field>
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink)', margin: '6px 0' }}>
            Floors — add each floor &amp; its drawing (DWG / DXF / PDF / image)
          </div>
          <FloorPlanRows
            floors={v.floors}
            uploadLabel="Drawing"
            setFloor={(fi, patch) => setFloorOn(vi, v.floors, fi, patch)}
            addFloor={() => setVersion(vi, { floors: [...(v.floors.length ? v.floors : [blankFloor()]), blankFloor()] })}
            removeFloor={(fi) => setVersion(vi, { floors: (v.floors.length ? v.floors : [blankFloor()]).filter((_, j) => j !== fi) })}
          />
        </div>
      ))}
      {multiVersion && (
        <Btn3 intent="ink" size="sm" onClick={addVersion}>
          + Add a revised version (same location &amp; north)
        </Btn3>
      )}
      <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>
        {multiVersion
          ? 'Add drawings any time during the project — each is a new version of the same property (location & north above stay fixed). e.g. Concept v1 → Concept v2 → Developed.'
          : 'L1C reviews one drawing set. Want 2–3 alternative plans reviewed? That’s a small paid add-on. Our team grids from your plan — you don’t draw anything.'}
      </div>

      {/* COORDINATION moved OUT of the drawings block (2026-06-25, BATCH 1). The
          architect / interior-designer / consultant coordination is now a
          repeatable, role-tagged, contact-linked list inside the Design brief
          (design_programme · "Coordination · project team"). L1C still shows the
          bright-line independent-review note; coordination tiers (L2C/L2A/…) get a
          short pointer to the design-brief section. */}
      {showArchitect ? (
        <div
          style={{
            fontSize: 12,
            color: 'var(--accent-deep, #274b63)',
            background: 'var(--accent-wash, #eef3f8)',
            border: '1px solid var(--accent-line, #d3e0ec)',
            borderRadius: 'var(--r-sm)',
            padding: '10px 12px',
            marginTop: 12,
          }}
        >
          🤝 <b>Your project team</b> (architect, interior designer, contractor, consultants) is captured in the
          <b> Design brief → Coordination · project team</b> step — each linked to your contacts so we can coordinate.
        </div>
      ) : (
        <div
          style={{
            fontSize: 11.5,
            color: 'var(--warn-deep, #9a5b00)',
            background: 'var(--warn-wash, #fffaf0)',
            borderLeft: '3px solid var(--warn, #c9892a)',
            borderRadius: '0 var(--r-sm) var(--r-sm) 0',
            padding: '8px 12px',
            marginTop: 12,
          }}
        >
          <b>This is an independent review.</b> We don't contact your architect on L1C. Need us to draft
          the fixes into architect-ready drawings and coordinate them with your architect on a joint
          call? That's <b>The Integration Path · Samanvaya (L2C)</b> — and your ₹11,000 is credited
          toward it within 60 days.
        </div>
      )}
    </Box>
  );
}

/* ───────────────── REAL: Design Programme (A→H, AD-040) ───────────────── */

/** A small section header card (mirrors the mockup's lettered card head). */
function DPSection({ letter, title, desc, required, optional, children }: { letter: string; title: string; desc?: string; required?: boolean; optional?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', marginBottom: 14, overflow: 'hidden', background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderBottom: '1px solid var(--rule)', background: 'var(--bg)' }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--accent, #2f6f8f)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flex: '0 0 auto' }}>{letter}</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            {title}
            {required && <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', borderRadius: 999, padding: '1px 7px', background: 'var(--red-wash, #fbedec)', color: 'var(--red, #9a3535)' }}>REQUIRED</span>}
            {optional && <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', borderRadius: 999, padding: '1px 7px', background: 'var(--bg, #f6f8f9)', color: 'var(--ink-mute)' }}>OPTIONAL</span>}
          </div>
          {desc && <div style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>{desc}</div>}
        </div>
      </div>
      <div style={{ padding: '14px' }}>{children}</div>
    </div>
  );
}

const dpChip = (on: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--rule)',
  background: on ? 'var(--ok-wash, #eaf5ef)' : 'var(--surface)', borderColor: on ? 'var(--ok-line, #bfe0cf)' : 'var(--rule)',
  color: on ? '#15543c' : 'var(--ink)', fontWeight: on ? 600 : 400,
  borderRadius: 999, padding: '6px 11px', fontSize: 12.5, cursor: 'pointer',
});
const dpCheck: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)', padding: '8px 10px', fontSize: 12.5, background: 'var(--surface)',
};

function DesignProgrammeModule({ form, readOnly, h, serviceTier }: { form: IntakeForm; readOnly: boolean; h: IntakeModuleHandlers; serviceTier?: string }) {
  const p: DesignProgrammeInput = form.designProgramme || blankDesignProgramme();
  // BATCH 2 — per-tier design-brief SECTION FLAGS (the deferred H mechanism). A
  // section can be required / optional / on / off per tier. L2A is configured;
  // others default to full depth.
  const flags: DesignBriefSectionFlags = useMemo(() => designBriefFlagsForTier(serviceTier || ''), [serviceTier]);
  // BATCH 1 — contact spine + shared roles for the Coordination section. Both
  // STAFF-only listeners (clients never link registry contacts). Lazy: only when
  // the coordination section is shown for this tier.
  const showCoordination = sectionShown(flags.coordination);
  const [allContacts, setAllContacts] = useState<ContactLite[]>([]);
  const [roles, setRoles] = useState<string[]>([...COORDINATION_ROLES]);
  const [coMsg, setCoMsg] = useState<string | null>(null);
  useEffect(() => {
    if (readOnly || !showCoordination) return;
    const unsubC = listenClients(
      (cs) => setAllContacts(cs.map((c) => ({ cid: c.cid, clientNo: c.clientNo, name: c.name, phone: c.phone, email: c.email, phones: c.phones, emails: c.emails }))),
      () => {},
    );
    const unsubR = listenCoordinationRoles(setRoles, () => {});
    return () => { unsubC(); unsubR(); };
  }, [readOnly, showCoordination]);
  function coordMsg(m: string) { setCoMsg(m); setTimeout(() => setCoMsg((x) => (x === m ? null : x)), 4500); }

  function patchP(patch: Partial<DesignProgrammeInput>) {
    h.patch({ designProgramme: { ...p, ...patch } });
  }
  function toggleIn(list: string[], v: string): string[] {
    return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
  }

  if (readOnly) {
    const total = spaceScheduleTotal(p.spaces);
    return (
      <Box title="Design programme" note="Saved">
        {p.spaces.filter((s) => s.space).length > 0 && (
          <ReadRow k="Spaces" v={`${p.spaces.filter((s) => s.space).map((s) => `${s.space}${s.qty && s.qty !== '1' ? ` ×${s.qty}` : ''}`).join(', ')}${total ? ` · ~${total} sq ft` : ''}`} />
        )}
        {p.householdType && <ReadRow k="Household" v={[p.householdType, p.wfh && `WFH: ${p.wfh}`, p.hosting && `Hosting: ${p.hosting}`].filter(Boolean).join(' · ')} />}
        {p.futureProofing.length > 0 && <ReadRow k="Future-proofing" v={p.futureProofing.join(', ')} />}
        {p.prioritySpaces.length > 0 && <ReadRow k="Top priorities" v={p.prioritySpaces.join(' › ')} />}
        {p.adjacencies.filter((a) => a.a && a.b).length > 0 && <ReadRow k="Adjacencies" v={p.adjacencies.filter((a) => a.a && a.b).map((a) => `${a.kind.toUpperCase()}: ${a.a}↔${a.b}`).join(', ')} />}
        {p.specialFunctions.length > 0 && <ReadRow k="Special functions" v={p.specialFunctions.map((f) => `${f.name}${f.qty ? ` (${f.qty})` : ''}`).join(', ')} />}
        {p.specialFunctionsNote && <ReadRow k="Other functions" v={p.specialFunctionsNote} />}
        {(p.archStyle || p.interiorLeaning) && <ReadRow k="Style" v={[p.archStyle, p.interiorLeaning].filter(Boolean).join(' · ')} />}
        {p.materials.length > 0 && <ReadRow k="Materials" v={p.materials.join(', ')} />}
        {p.references && <ReadRow k="References" v={p.references} />}
        {(p.scopeOfWork || p.budgetRange) && <ReadRow k="Scope & budget" v={[p.scopeOfWork, p.budgetRange, p.phasing, p.desiredStart].filter(Boolean).join(' · ')} />}
        {p.constraints.length > 0 && <ReadRow k="Constraints" v={p.constraints.join(', ')} />}
        {p.mustKeeps && <ReadRow k="Must-keeps" v={p.mustKeeps} />}
        {(p.coordination || []).filter((m) => m.contactId || (m.name || '').trim()).length > 0 && (
          <ReadRow
            k="Project team"
            v={(p.coordination || [])
              .filter((m) => m.contactId || (m.name || '').trim())
              .map((m) => `${m.role}: ${m.name || m.contactId}${m.contactId ? ` (${m.contactId})` : ''}`)
              .join(' · ')}
          />
        )}
      </Box>
    );
  }

  const total = spaceScheduleTotal(p.spaces);
  const spaces = p.spaces.length ? p.spaces : [blankSpaceRow()];
  function setSpace(i: number, patch: Partial<SpaceRow>) {
    const base = p.spaces.length ? p.spaces : spaces;
    const next = base.slice();
    next[i] = { ...next[i], ...patch };
    patchP({ spaces: next });
  }
  function addSpace() { patchP({ spaces: [...(p.spaces.length ? p.spaces : spaces), blankSpaceRow()] }); }
  function removeSpace(i: number) { const base = p.spaces.length ? p.spaces : spaces; patchP({ spaces: base.filter((_, j) => j !== i) }); }
  function quickAddSpace(name: string) { patchP({ spaces: [...(p.spaces.length ? p.spaces : []), { ...blankSpaceRow(), space: name }] }); }

  const adjacencies = p.adjacencies;
  function setAdj(i: number, patch: Partial<AdjacencyRule>) { const next = adjacencies.slice(); next[i] = { ...next[i], ...patch }; patchP({ adjacencies: next }); }
  function addAdj() { patchP({ adjacencies: [...adjacencies, blankAdjacency()] }); }
  function removeAdj(i: number) { patchP({ adjacencies: adjacencies.filter((_, j) => j !== i) }); }

  // Special functions: a checklist; "Car parking" carries a qty.
  function toggleFunction(name: string) {
    const exists = p.specialFunctions.some((f) => f.name === name);
    patchP({
      specialFunctions: exists
        ? p.specialFunctions.filter((f) => f.name !== name)
        : [...p.specialFunctions, { name }],
    });
  }
  function setFunctionQty(name: string, qty: string) {
    patchP({ specialFunctions: p.specialFunctions.map((f) => (f.name === name ? { ...f, qty } : f)) });
  }
  const fnSelected = (name: string) => p.specialFunctions.find((f) => f.name === name);

  // Priority spaces — pick from the named spaces (top 3, ranked by selection order).
  const namedSpaces = (p.spaces.length ? p.spaces : []).map((s) => s.space).filter(Boolean);
  function togglePriority(name: string) {
    if (p.prioritySpaces.includes(name)) { patchP({ prioritySpaces: p.prioritySpaces.filter((x) => x !== name) }); return; }
    if (p.prioritySpaces.length >= 3) return; // cap at 3
    patchP({ prioritySpaces: [...p.prioritySpaces, name] });
  }

  // H · COORDINATION — repeatable role-tagged contact-linked rows.
  const coordination = p.coordination || [];
  function setCoord(i: number, patch: Partial<CoordinationMember>) {
    const next = coordination.slice();
    next[i] = { ...next[i], ...patch };
    patchP({ coordination: next });
  }
  function addCoord() { patchP({ coordination: [...coordination, blankCoordinationMember(roles[0] || 'Architect')] }); }
  function removeCoord(i: number) { patchP({ coordination: coordination.filter((_, j) => j !== i) }); }
  async function saveCustomRole(i: number, name: string) {
    const v = name.trim();
    if (!v) return;
    setCoord(i, { role: v });
    try { await addCoordinationRole(v); coordMsg(`Role "${v}" saved to the shared list.`); } catch { /* best-effort */ }
  }

  return (
    <Box title="Design programme" note="The requirements brief — what you want, who it’s for, and how." allowOverflow>
      <p style={{ fontSize: 12.5, color: 'var(--ink-mute)', margin: '0 0 12px' }}>
        The requirements brief. Site / plot / regulatory data is pulled from the Property block — not re-asked here.
        {serviceTier ? <span style={{ marginLeft: 6, color: 'var(--accent-deep, #274b63)' }}>Sections shown for this tier are tailored to {serviceTier.replace(/_R\d+$/i, '')}.</span> : null}
      </p>
      {coMsg && (
        <div style={{ padding: '6px 10px', background: 'var(--bg)', borderRadius: 'var(--r-sm)', fontSize: 12.5, marginBottom: 10, border: '1px solid var(--rule)' }}>{coMsg}</div>
      )}

      {/* A · SPACE SCHEDULE */}
      {sectionShown(flags.spaceSchedule) && (
      <DPSection letter="A" title="Space schedule" required={sectionRequired(flags.spaceSchedule)} desc="Every room / space — quantity, target size, floor, and how essential it is.">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', alignSelf: 'center' }}>Quick add:</span>
          {['Living room', 'Dining', 'Kitchen', 'Master bedroom', 'Bedroom', 'Pooja room', 'Study', 'Toilet', 'Balcony'].map((n) => (
            <button key={n} type="button" style={{ border: '1px dashed var(--rule-strong)', background: 'var(--surface)', borderRadius: 999, padding: '5px 10px', fontSize: 12, color: 'var(--link)', cursor: 'pointer', fontWeight: 600 }} onClick={() => quickAddSpace(n)}>＋ {n}</button>
          ))}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 520 }}>
            <thead>
              <tr>
                {['Space', 'Qty', 'Target area (sq ft)', 'Floor', 'Priority', ''].map((th) => (
                  <th key={th} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--rule)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--ink-mute)', fontWeight: 700 }}>{th}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {spaces.map((s, i) => (
                <tr key={s.id}>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--rule)' }}>
                    <input style={{ ...fieldStyle, padding: '6px 8px' }} value={s.space} onChange={(e) => setSpace(i, { space: e.target.value })} placeholder="e.g. Living room" />
                  </td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--rule)' }}>
                    <input style={{ ...fieldStyle, padding: '6px 8px', width: 56 }} value={s.qty} onChange={(e) => setSpace(i, { qty: e.target.value })} inputMode="numeric" />
                  </td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--rule)' }}>
                    <input style={{ ...fieldStyle, padding: '6px 8px', width: 90 }} value={s.targetArea} onChange={(e) => setSpace(i, { targetArea: e.target.value })} inputMode="numeric" placeholder="—" />
                  </td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--rule)' }}>
                    <select style={{ ...fieldStyle, padding: '6px 8px' }} value={s.floor} onChange={(e) => setSpace(i, { floor: e.target.value })}>
                      <option value="">—</option>
                      {FLOOR_IDS.map((id) => <option key={id} value={FLOOR_LABELS[id]}>{FLOOR_LABELS[id]}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--rule)' }}>
                    <select style={{ ...fieldStyle, padding: '6px 8px' }} value={s.priority} onChange={(e) => setSpace(i, { priority: e.target.value as SpaceRow['priority'] })}>
                      <option value="">—</option>
                      {SPACE_PRIORITIES.map((pr) => <option key={pr} value={pr}>{pr}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--rule)' }}>
                    <button type="button" style={{ width: 22, height: 22, borderRadius: '50%', border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink-mute)', cursor: 'pointer' }} onClick={() => removeSpace(i)} aria-label="Remove space">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, flexWrap: 'wrap', gap: 8 }}>
          <button type="button" style={{ border: '1px dashed var(--rule-strong)', background: 'var(--surface)', borderRadius: 999, padding: '6px 12px', fontSize: 12.5, color: 'var(--link)', cursor: 'pointer', fontWeight: 600 }} onClick={addSpace}>＋ Add a space</button>
          <span style={{ color: 'var(--ink-mute)', fontSize: 12.5 }}>Running total: <b style={{ color: 'var(--ink)' }}>{total ? `${total} sq ft` : '—'}</b> across {p.spaces.filter((s) => s.space).length} spaces</span>
        </div>
      </DPSection>
      )}

      {/* B · LIFESTYLE */}
      {sectionShown(flags.lifestyle) && (
      <DPSection letter="B" title="Who it’s for · lifestyle" required={sectionRequired(flags.lifestyle)} optional={flags.lifestyle === 'optional'} desc="Occupants are pulled from the project’s People; here we capture how they live.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label="Household type" optional>
            <select style={fieldStyle} value={p.householdType} onChange={(e) => patchP({ householdType: e.target.value })}>
              <option value="">Select…</option>
              {HOUSEHOLD_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Work-from-home?" optional>
            <select style={fieldStyle} value={p.wfh} onChange={(e) => patchP({ wfh: e.target.value })}>
              <option value="">Select…</option>
              {WFH_OPTIONS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Guests / hosting" optional>
            <select style={fieldStyle} value={p.hosting} onChange={(e) => patchP({ hosting: e.target.value })}>
              <option value="">Select…</option>
              {HOSTING_OPTIONS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.5px', margin: '14px 0 8px' }}>Future-proofing</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {FUTURE_PROOFING.map((f) => (
            <label key={f} style={dpCheck}>
              <input type="checkbox" checked={p.futureProofing.includes(f)} onChange={() => patchP({ futureProofing: toggleIn(p.futureProofing, f) })} /> {f}
            </label>
          ))}
        </div>
      </DPSection>
      )}

      {/* C · ADJACENCIES & PRIORITIES */}
      {sectionShown(flags.adjacencies) && (
      <DPSection letter="C" title="Adjacencies &amp; priorities" required={sectionRequired(flags.adjacencies)} desc="What must sit near (or far from) what — and the spaces that matter most.">
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.5px', margin: '0 0 8px' }}>Top priority spaces — pick up to 3 (in order)</div>
        {namedSpaces.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--ink-mute)' }}>Add spaces in section A first, then rank the top 3 here.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {namedSpaces.map((n) => {
              const rank = p.prioritySpaces.indexOf(n);
              const on = rank >= 0;
              return (
                <button key={n} type="button" style={dpChip(on)} onClick={() => togglePriority(n)}>
                  {on && <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--accent, #2f6f8f)', color: '#fff', fontWeight: 700, fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{rank + 1}</span>}
                  {n}
                </button>
              );
            })}
          </div>
        )}
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.5px', margin: '16px 0 8px' }}>Adjacency rules</div>
        {adjacencies.map((a, i) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0', flexWrap: 'wrap' }}>
            <select style={{ ...fieldStyle, width: 92 }} value={a.kind} onChange={(e) => setAdj(i, { kind: e.target.value as AdjacencyRule['kind'] })}>
              {ADJACENCY_KINDS.map((k) => <option key={k} value={k}>{k.toUpperCase()}</option>)}
            </select>
            <input style={{ ...fieldStyle, flex: 1, minWidth: 120 }} value={a.a} onChange={(e) => setAdj(i, { a: e.target.value })} placeholder="space" />
            <span style={{ color: 'var(--ink-mute)' }}>↔</span>
            <input style={{ ...fieldStyle, flex: 1, minWidth: 120 }} value={a.b} onChange={(e) => setAdj(i, { b: e.target.value })} placeholder="space" />
            <button type="button" style={{ width: 22, height: 22, borderRadius: '50%', border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink-mute)', cursor: 'pointer' }} onClick={() => removeAdj(i)}>✕</button>
          </div>
        ))}
        <button type="button" style={{ border: '1px dashed var(--rule-strong)', background: 'var(--surface)', borderRadius: 999, padding: '6px 12px', fontSize: 12.5, color: 'var(--link)', cursor: 'pointer', fontWeight: 600, marginTop: 4 }} onClick={addAdj}>＋ Add a rule</button>
      </DPSection>
      )}

      {/* D · SPECIAL FUNCTIONS */}
      {sectionShown(flags.specialFunctions) && (
      <DPSection letter="D" title="Special functions &amp; amenities" required={sectionRequired(flags.specialFunctions)} desc="Beyond the core rooms — the specific facilities this household needs.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {SPECIAL_FUNCTIONS.map((name) => {
            const sel = fnSelected(name);
            const hasQty = name === 'Car parking';
            return (
              <label key={name} style={dpCheck}>
                <input type="checkbox" checked={!!sel} onChange={() => toggleFunction(name)} /> {name}
                {hasQty && sel && (
                  <input style={{ ...fieldStyle, marginLeft: 'auto', width: 46, padding: '4px 6px' }} value={sel.qty || ''} onChange={(e) => setFunctionQty(name, e.target.value)} inputMode="numeric" placeholder="#" />
                )}
              </label>
            );
          })}
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label="Anything else specific to this household?" optional>
            <textarea style={{ ...fieldStyle, minHeight: 48, resize: 'vertical' }} rows={2} value={p.specialFunctionsNote} onChange={(e) => patchP({ specialFunctionsNote: e.target.value })} placeholder="e.g. a music room, a separate clinic entry, a cattle shed…" />
          </Field>
        </div>
      </DPSection>
      )}

      {/* E · STYLE */}
      {sectionShown(flags.style) && (
      <DPSection letter="E" title="Style &amp; aesthetic" required={sectionRequired(flags.style)} desc="The look and feel — typology, references, material leanings.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Architectural style" optional>
            <select style={fieldStyle} value={p.archStyle} onChange={(e) => patchP({ archStyle: e.target.value })}>
              <option value="">Select…</option>
              {ARCH_STYLES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Interior leaning" optional>
            <select style={fieldStyle} value={p.interiorLeaning} onChange={(e) => patchP({ interiorLeaning: e.target.value })}>
              <option value="">Select…</option>
              {INTERIOR_LEANINGS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.5px', margin: '14px 0 8px' }}>Material &amp; finish preferences</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {MATERIAL_PREFS.map((m) => (
            <button key={m} type="button" style={dpChip(p.materials.includes(m))} onClick={() => patchP({ materials: toggleIn(p.materials, m) })}>{m}</button>
          ))}
        </div>
        <div style={{ marginTop: 14 }}>
          <Field label="Reference images / Pinterest / notes" optional>
            <input style={fieldStyle} value={p.references} onChange={(e) => patchP({ references: e.target.value })} placeholder="Paste a link, or describe the look you want" />
          </Field>
        </div>
      </DPSection>
      )}

      {/* F · BUDGET & SCOPE */}
      {sectionShown(flags.budget) && (
      <DPSection letter="F" title="Budget &amp; scope" required={sectionRequired(flags.budget)} desc="What kind of project, how much, and over what timeline.">
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.5px', margin: '0 0 8px' }}>Scope of work</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {SCOPE_OF_WORK.map((s) => (
            <button key={s} type="button" style={dpChip(p.scopeOfWork === s)} onClick={() => patchP({ scopeOfWork: p.scopeOfWork === s ? '' : s })}>{s}</button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label="Budget range" optional>
            <select style={fieldStyle} value={p.budgetRange} onChange={(e) => patchP({ budgetRange: e.target.value })}>
              <option value="">Select…</option>
              {BUDGET_RANGES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Build phasing" optional>
            <select style={fieldStyle} value={p.phasing} onChange={(e) => patchP({ phasing: e.target.value })}>
              <option value="">Select…</option>
              {PHASING_OPTIONS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Desired start" optional>
            <select style={fieldStyle} value={p.desiredStart} onChange={(e) => patchP({ desiredStart: e.target.value })}>
              <option value="">Select…</option>
              {START_OPTIONS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 8 }}>Budget keeps the brief realistic against the FAR-permitted area — never shown in the client report unless asked. “Prefer not to say” is always allowed.</div>
      </DPSection>
      )}

      {/* G · CONSTRAINTS */}
      {sectionShown(flags.constraints) && (
      <DPSection letter="G" title="Constraints &amp; must-keeps" required={sectionRequired(flags.constraints)} desc="Anything fixed on site the design has to work around or preserve.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {CONSTRAINT_CHECKS.map((c) => (
            <label key={c} style={dpCheck}>
              <input type="checkbox" checked={p.constraints.includes(c)} onChange={() => patchP({ constraints: toggleIn(p.constraints, c) })} /> {c}
            </label>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label="Describe the must-keeps / fixed constraints" optional>
            <textarea style={{ ...fieldStyle, minHeight: 48, resize: 'vertical' }} rows={2} value={p.mustKeeps} onChange={(e) => patchP({ mustKeeps: e.target.value })} placeholder="e.g. retain the existing pooja location, keep the 30-yr-old mango tree in the SE…" />
          </Field>
        </div>
      </DPSection>
      )}

      {/* H · COORDINATION · project team — repeatable, role-tagged, contact-linked.
          Coordination tiers only (config: flags.coordination !== 'off'); never L1C. */}
      {showCoordination && (
      <DPSection letter="H" title="Coordination · your project team 🤝" required={sectionRequired(flags.coordination)} desc="Everyone we’ll coordinate with — link an existing contact or add new. No fixed limit, no duplicates.">
        {coordination.length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--ink-mute)', margin: '0 0 10px' }}>No team members yet. Add the architect, interior designer, contractor, consultants — each linked to your contacts.</p>
        )}
        {coordination.map((m, i) => {
          const isCustomRole = !(COORDINATION_ROLES as readonly string[]).includes(m.role) && m.role !== 'Other…' && !!m.role;
          const selectValue = (roles.includes(m.role) || COORDINATION_ROLES.includes(m.role as never)) ? m.role : (m.role ? 'Other…' : roles[0]);
          return (
            <div key={m.id} style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 12, marginBottom: 10, background: 'var(--surface)' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ width: 220 }}>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>Role on this project</label>
                  <select
                    style={{ width: '100%', border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', padding: '8px 10px', fontSize: 13 }}
                    value={selectValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'Other…') { setCoord(i, { role: 'Other…' }); }
                      else setCoord(i, { role: v });
                    }}
                  >
                    {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                    <option value="Other…">Other…</option>
                  </select>
                </div>
                {(selectValue === 'Other…' || isCustomRole) && (
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>Name this role <span style={{ fontSize: 9.5, fontWeight: 700, borderRadius: 999, padding: '1px 7px', background: 'var(--accent-wash, #eef3f8)', color: 'var(--accent-deep, #274b63)' }}>custom</span></label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        style={{ flex: 1, border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', padding: '8px 10px', fontSize: 13 }}
                        defaultValue={isCustomRole ? m.role : ''}
                        placeholder="e.g. Vastu liaison (client side)"
                        onBlur={(e) => { if (e.target.value.trim()) void saveCustomRole(i, e.target.value); }}
                      />
                    </div>
                  </div>
                )}
                <button type="button" style={{ width: 22, height: 22, borderRadius: '50%', border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink-mute)', cursor: 'pointer' }} onClick={() => removeCoord(i)} aria-label="Remove team member">✕</button>
              </div>
              <ContactSlotLink
                member={m}
                allContacts={allContacts}
                actorName="Staff"
                onPatch={(patch) => setCoord(i, patch)}
                onMsg={(msg) => coordMsg(msg)}
              />
            </div>
          );
        })}
        <button type="button" style={{ display: 'block', width: '100%', border: '1px dashed var(--rule-strong)', background: 'var(--surface)', borderRadius: 'var(--r-sm)', padding: 11, textAlign: 'left', fontSize: 12.5, color: 'var(--link)', cursor: 'pointer', fontWeight: 600 }} onClick={addCoord}>
          ＋ Add a team member
        </button>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 8 }}>
          Same pattern as Properties: each row <b>links an existing contact</b> or <b>adds a new one</b>, role-tagged for this project. Even on "add new", we check the spine live (by name / phone / email) and surface any likely match so you decide — never silently duplicated. Roles are open-ended; <b>"Other…" names &amp; saves a new role</b>. Coordination tiers (L2C / L2A / L3A / L3B) only, never L1C.
        </div>
      </DPSection>
      )}
    </Box>
  );
}

/* ───────────────── REAL: Construction details (L2D, 11 groups, AD-042) ───────────────── */

/** A labelled <select> bound to a string field. */
function CDSelect({ label, value, opts, onChange, optional }: { label: string; value: string; opts: readonly string[]; onChange: (v: string) => void; optional?: boolean }) {
  return (
    <Field label={label} optional={optional}>
      <select style={fieldStyle} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}

function CDGroup({ n, title, deeper, children }: { n: string; title: string; deeper?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 12, margin: '10px 0', background: 'var(--surface)' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.5px', margin: '0 0 9px', display: 'flex', alignItems: 'center', gap: 8 }}>
        {n} {title}
        {deeper && <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--link)', fontWeight: 600, border: '1px dashed var(--rule-strong)', borderRadius: 999, padding: '2px 9px' }}>＋ detailed spec (optional)</span>}
      </div>
      {children}
    </div>
  );
}

function ConstructionDetailsModule({ form, readOnly, h }: { form: IntakeForm; readOnly: boolean; h: IntakeModuleHandlers }) {
  const c: ConstructionDetailsInput = form.constructionDetails || blankConstructionDetails();
  const set = (patch: Partial<ConstructionDetailsInput>) => h.patch({ constructionDetails: { ...c, ...patch } });
  const togglePrep = (name: string) => set({ prepared: c.prepared.includes(name) ? c.prepared.filter((x) => x !== name) : [...c.prepared, name] });
  const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };
  const grid3: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 };

  if (readOnly) {
    const rows: [string, string][] = [
      ['Stage', c.drawingStage], ['Structure', c.structuralSystem], ['Floors', c.floors], ['Built-up', c.builtUpSqft],
      ['Prepared', c.prepared.join(', ')], ['Water', [c.waterSource, c.ugTank && `UG:${c.ugTank}`, c.ohTank && `OH:${c.ohTank}`, c.waterNotes].filter(Boolean).join(' · ')],
      ['Electrical', [c.elecSource, c.backupPower, c.solarProvision].filter(Boolean).join(' · ')],
      ['HVAC', [c.hvacApproach, c.hvacAreas, c.hvacNotes].filter(Boolean).join(' · ')],
      ['Ceiling/lighting', [c.falseCeiling, c.lightingApproach].filter(Boolean).join(' · ')],
      ['Materials', [c.specLevel, c.plumbingGrade && `plumb:${c.plumbingGrade}`, c.electricalGrade && `elec:${c.electricalGrade}`, c.materialNotes].filter(Boolean).join(' · ')],
      ['Doors/windows', [c.windowSystem, c.doorTypes].filter(Boolean).join(' · ')],
      ['Soil/geology', [c.soilType, c.geotech, c.waterTable, c.soilNotes].filter(Boolean).join(' · ')],
      ['Construction tech', [c.constructionMethod, c.localMaterialNotes].filter(Boolean).join(' · ')],
      ['Approvals/site', [c.statutoryApproval, c.siteCondition, c.targetStart, c.specificNote].filter(Boolean).join(' · ')],
    ];
    return <Box title="Construction details" note="Saved">{rows.filter(([, v]) => (v || '').trim()).map(([k, v]) => <ReadRow key={k} k={k} v={v} />)}</Box>;
  }

  return (
    <Box title="Construction details" note="Everything the detailing team needs — captured light, with a deeper-spec note where it's critical." allowOverflow>
      <CDGroup n="①" title="Drawing stage & structure">
        <div style={grid2}>
          <CDSelect label="Stage of drawings provided" value={c.drawingStage} opts={DRAWING_STAGE_OPTS} onChange={(v) => set({ drawingStage: v })} />
          <CDSelect label="Structural system" value={c.structuralSystem} opts={STRUCTURAL_SYSTEMS} onChange={(v) => set({ structuralSystem: v })} />
          <Field label="Floors (incl. stilt / basement)" optional><input style={fieldStyle} value={c.floors} onChange={(e) => set({ floors: e.target.value })} placeholder="e.g. G + 2 + terrace" /></Field>
          <Field label="Built-up (sq ft)" optional><input style={fieldStyle} value={c.builtUpSqft} onChange={(e) => set({ builtUpSqft: e.target.value })} inputMode="numeric" placeholder="e.g. 3200" /></Field>
        </div>
      </CDGroup>

      <CDGroup n="②" title="What's already prepared">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {PREPARED_CHECKS.map((p) => (
            <label key={p} style={dpCheck}><input type="checkbox" checked={c.prepared.includes(p)} onChange={() => togglePrep(p)} /> {p}</label>
          ))}
        </div>
      </CDGroup>

      <CDGroup n="③" title="Water & plumbing" deeper>
        <div style={grid3}>
          <CDSelect label="Water availability & source" value={c.waterSource} opts={WATER_SOURCES} onChange={(v) => set({ waterSource: v })} />
          <CDSelect label="Underground tank?" value={c.ugTank} opts={YES_NO} onChange={(v) => set({ ugTank: v })} />
          <CDSelect label="Overhead tank?" value={c.ohTank} opts={YES_NO} onChange={(v) => set({ ohTank: v })} />
        </div>
        <Field label="Tank capacities / source notes" optional><input style={fieldStyle} value={c.waterNotes} onChange={(e) => set({ waterNotes: e.target.value })} placeholder="e.g. UG 10,000 L + OH 2,000 L; borewell 250 ft" /></Field>
      </CDGroup>

      <CDGroup n="④" title="Electrical & power">
        <div style={grid3}>
          <CDSelect label="Source of electricity" value={c.elecSource} opts={ELEC_SOURCES} onChange={(v) => set({ elecSource: v })} />
          <CDSelect label="Backup power" value={c.backupPower} opts={BACKUP_POWER} onChange={(v) => set({ backupPower: v })} />
          <CDSelect label="Solar provision" value={c.solarProvision} opts={SOLAR_PROVISION} onChange={(v) => set({ solarProvision: v })} />
        </div>
      </CDGroup>

      <CDGroup n="⑤" title="HVAC" deeper>
        <div style={grid2}>
          <CDSelect label="HVAC approach" value={c.hvacApproach} opts={HVAC_APPROACH} onChange={(v) => set({ hvacApproach: v })} />
          <Field label="Areas to be conditioned" optional><input style={fieldStyle} value={c.hvacAreas} onChange={(e) => set({ hvacAreas: e.target.value })} placeholder="e.g. all bedrooms + living; not kitchen" /></Field>
        </div>
        <Field label="HVAC notes (deeper spec)" optional><input style={fieldStyle} value={c.hvacNotes} onChange={(e) => set({ hvacNotes: e.target.value })} placeholder="tonnage, ducting, zoning…" /></Field>
      </CDGroup>

      <CDGroup n="⑥" title="Ceiling & lighting">
        <div style={grid2}>
          <CDSelect label="False ceiling" value={c.falseCeiling} opts={FALSE_CEILING} onChange={(v) => set({ falseCeiling: v })} />
          <CDSelect label="Lighting approach (intent)" value={c.lightingApproach} opts={LIGHTING_APPROACH} onChange={(v) => set({ lightingApproach: v })} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>We capture the lighting <i>intent</i> here; the fixture layout is produced in the detailing.</div>
      </CDGroup>

      <CDGroup n="⑦" title="Materials, finishes & MEP grade" deeper>
        <div style={grid3}>
          <CDSelect label="Overall spec level" value={c.specLevel} opts={SPEC_LEVEL} onChange={(v) => set({ specLevel: v })} />
          <CDSelect label="Plumbing fixtures grade" value={c.plumbingGrade} opts={PLUMB_GRADE} onChange={(v) => set({ plumbingGrade: v })} />
          <CDSelect label="Electrical fixtures grade" value={c.electricalGrade} opts={ELEC_GRADE} onChange={(v) => set({ electricalGrade: v })} />
        </div>
        <Field label="Preferred brands / finishes notes" optional><input style={fieldStyle} value={c.materialNotes} onChange={(e) => set({ materialNotes: e.target.value })} placeholder="e.g. Jaquar fittings, vitrified flooring, veneer doors…" /></Field>
      </CDGroup>

      <CDGroup n="⑧" title="Doors & windows">
        <div style={grid2}>
          <CDSelect label="Window system" value={c.windowSystem} opts={WINDOW_SYSTEM} onChange={(v) => set({ windowSystem: v })} />
          <CDSelect label="Door types" value={c.doorTypes} opts={DOOR_TYPES} onChange={(v) => set({ doorTypes: v })} />
        </div>
      </CDGroup>

      <CDGroup n="⑨" title="Soil & geology" deeper>
        <div style={grid3}>
          <CDSelect label="Soil type (if known)" value={c.soilType} opts={SOIL_TYPES} onChange={(v) => set({ soilType: v })} />
          <CDSelect label="Geotech report?" value={c.geotech} opts={GEOTECH} onChange={(v) => set({ geotech: v })} />
          <CDSelect label="Water table / flooding" value={c.waterTable} opts={WATER_TABLE} onChange={(v) => set({ waterTable: v })} />
        </div>
        <Field label="Soil / geotech notes (deeper spec)" optional><input style={fieldStyle} value={c.soilNotes} onChange={(e) => set({ soilNotes: e.target.value })} placeholder="bearing capacity, foundation type…" /></Field>
      </CDGroup>

      <CDGroup n="⑩" title="Local construction technology">
        <div style={grid2}>
          <CDSelect label="Construction method" value={c.constructionMethod} opts={CONSTRUCTION_METHOD} onChange={(v) => set({ constructionMethod: v })} />
          <Field label="Local material availability notes" optional><input style={fieldStyle} value={c.localMaterialNotes} onChange={(e) => set({ localMaterialNotes: e.target.value })} placeholder="e.g. local stone abundant; steel from city" /></Field>
        </div>
      </CDGroup>

      <CDGroup n="⑪" title="Approvals, site & anything specific">
        <div style={grid3}>
          <CDSelect label="Statutory approval" value={c.statutoryApproval} opts={STATUTORY_APPROVAL} onChange={(v) => set({ statutoryApproval: v })} />
          <CDSelect label="Site condition" value={c.siteCondition} opts={SITE_CONDITION} onChange={(v) => set({ siteCondition: v })} />
          <CDSelect label="Target start" value={c.targetStart} opts={TARGET_START} onChange={(v) => set({ targetStart: v })} />
        </div>
        <Field label="Anything specific for detailing?" optional><textarea style={{ ...fieldStyle, minHeight: 48, resize: 'vertical' }} rows={2} value={c.specificNote} onChange={(e) => set({ specificNote: e.target.value })} placeholder="special foundation, slab-level changes, a consultant to coordinate with…" /></Field>
      </CDGroup>
    </Box>
  );
}

/* ───────────────── REAL: L3B Plans & preferences (6 questions, AD-042) ───────────────── */

function L3BPlansPreferences({ form, readOnly, h }: { form: IntakeForm; readOnly: boolean; h: IntakeModuleHandlers }) {
  const p: DesignPlansPrefsInput = form.designPlansPrefs || blankDesignPlansPrefs();
  const set = (patch: Partial<DesignPlansPrefsInput>) => h.patch({ designPlansPrefs: { ...p, ...patch } });

  if (readOnly) {
    const rows: [string, string][] = [
      ['Plan in mind', p.havePlan], ['Decision-maker', [p.deciderName, p.deciderRole].filter(Boolean).join(' · ')],
      ['Approval flow', p.approvalFlow], ['References', p.references], ['Non-negotiables', p.nonNegotiables], ['Vastu stance', p.vastuStrictness],
    ];
    return <Box title="Plans & preferences" note="Saved">{rows.filter(([, v]) => (v || '').trim()).map(([k, v]) => <ReadRow key={k} k={k} v={v} />)}</Box>;
  }

  const pillRow = (value: string, opts: readonly string[], onPick: (v: string) => void) => (
    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 4 }}>
      {opts.map((o) => {
        const on = value === o;
        return <button key={o} type="button" onClick={() => onPick(o)} style={{ border: on ? '1.5px solid var(--ok-deep, #0f6f4e)' : '1.5px solid var(--rule-strong)', background: on ? 'var(--ok-deep, #0f6f4e)' : 'var(--surface)', color: on ? '#fff' : 'var(--ink-mute)', borderRadius: 999, padding: '5px 13px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{o}</button>;
      })}
    </div>
  );

  return (
    <Box title="Plans & preferences" note="A few questions so the design lands right — your plan-in-mind, who decides, and how strictly Vastu should lead.">
      <Field label="1 · Do you already have a plan / design in mind?">
        {pillRow(p.havePlan, HAVE_PLAN_OPTS, (v) => set({ havePlan: v }))}
        {p.havePlan === 'Yes — I can share sketches / references' && (
          <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>Share sketches / references in the Drawings & plans stage or by message.</div>
        )}
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        <Field label="2 · Who makes the final design decision?"><input style={fieldStyle} value={p.deciderName} onChange={(e) => set({ deciderName: e.target.value })} placeholder="Name" /></Field>
        <Field label="Decision-maker type" optional>
          <select style={fieldStyle} value={p.deciderRole} onChange={(e) => set({ deciderRole: e.target.value })}>
            <option value="">Select…</option>
            {DECIDER_ROLE_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <Field label="3 · How should approvals run?" optional>
        {pillRow(p.approvalFlow, APPROVAL_FLOW_OPTS, (v) => set({ approvalFlow: v }))}
      </Field>
      <Field label="4 · Reference homes / projects / styles you love" optional>
        <textarea style={{ ...fieldStyle, minHeight: 48, resize: 'vertical' }} rows={2} value={p.references} onChange={(e) => set({ references: e.target.value })} placeholder="links, images, project names, styles…" />
      </Field>
      <Field label="5 · Your non-negotiables — must-haves we can't compromise" optional>
        <textarea style={{ ...fieldStyle, minHeight: 48, resize: 'vertical' }} rows={2} value={p.nonNegotiables} onChange={(e) => set({ nonNegotiables: e.target.value })} placeholder="e.g. pooja in the NE, a ground-floor bedroom for parents…" />
      </Field>
      <Field label="6 · How strictly should Vastu lead vs balance with aesthetics & space?">
        {pillRow(p.vastuStrictness, VASTU_STRICTNESS_OPTS, (v) => set({ vastuStrictness: v }))}
      </Field>
    </Box>
  );
}

/* ───────────────── L1B · 7 — Plans & preferences (willingness) ───────────────── */

/** The four willingness questions — id, label, helper, options, required. */
const WILLINGNESS_QUESTIONS: {
  id: 'implement' | 'structural' | 'discuss' | 'follow_process';
  label: string;
  optHint?: string;
  required: boolean;
  hint: string;
  options: string[];
}[] = [
  {
    id: 'implement',
    label: 'Willingness to implement the remedies',
    required: true,
    hint: 'A diagnosis only helps if the fixes get done. How ready are you to act on the plan?',
    options: ['Ready to act now', 'Will implement gradually', 'Just want to understand for now'],
  },
  {
    id: 'structural',
    label: 'Openness to structural change',
    optHint: '— only in the rarest case, if at all',
    required: false,
    hint: 'Most remedies need no construction. Occasionally a real fix needs a structural change — how open are you if it’s genuinely necessary?',
    options: ['Open if truly essential', 'Minor, non-structural only', 'No structural changes'],
  },
  {
    id: 'discuss',
    label: 'Willingness to discuss & give us your time',
    required: true,
    hint: 'The findings call is where the real value lands. Can you give us the time to walk through it together?',
    options: ['Yes, I’ll make the time', 'Limited time — keep it brief'],
  },
  {
    id: 'follow_process',
    label: 'Willingness to follow a proven process',
    required: true,
    hint: 'Our method has produced results for many clients. It works best when followed as a whole rather than picking pieces.',
    options: ['Yes — guide me through it', 'I’d like to choose what to follow'],
  },
];

function L1BPlansPreferences({
  form,
  readOnly,
  h,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
}) {
  const w = form.willingness || {};
  function pick(id: (typeof WILLINGNESS_QUESTIONS)[number]['id'], opt: string) {
    h.patch({ willingness: { ...w, [id]: opt } });
  }

  if (readOnly) {
    return (
      <Box title="Your plans & preferences" note="Saved">
        {(form.prior_remedies_tried || '').trim() && (
          <ReadRow k="Already tried" v={form.prior_remedies_tried} />
        )}
        {WILLINGNESS_QUESTIONS.map((q) => (
          <ReadRow key={q.id} k={q.label} v={w[q.id] || '—'} />
        ))}
      </Box>
    );
  }

  return (
    <Box title="Your plans & preferences" note="So we pitch the remedy plan at the right level — and make sure we're a good fit.">
      <Field label="What have you already tried?" optional>
        <textarea
          style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }}
          value={form.prior_remedies_tried}
          onChange={(e) => h.patch({ prior_remedies_tried: e.target.value })}
          placeholder="Any Vastu changes, remedies, pujas, renovations or advice you've already acted on — and whether it helped"
        />
      </Field>
      {WILLINGNESS_QUESTIONS.map((q) => (
        <div
          key={q.id}
          style={{
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            padding: '11px 13px',
            marginBottom: 10,
            background: 'var(--surface)',
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)', marginBottom: 3 }}>
            {q.label}
            {q.required ? reqMark : q.optHint ? <span style={{ color: 'var(--ink-mute)', fontWeight: 500 }}> {q.optHint}</span> : null}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginBottom: 7 }}>{q.hint}</div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {q.options.map((opt) => {
              const on = w[q.id] === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => pick(q.id, opt)}
                  style={{
                    border: on ? '1.5px solid var(--ok-deep, #0f6f4e)' : '1.5px solid var(--rule-strong)',
                    background: on ? 'var(--ok-deep, #0f6f4e)' : 'var(--surface)',
                    color: on ? '#fff' : 'var(--ink-mute)',
                    borderRadius: 999,
                    padding: '5px 13px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </Box>
  );
}

/* ─────────────────── L1A · 5 — People (auto chart, masked birth) ─────────────────── */

function L1APeople({
  form,
  readOnly,
  h,
  viewerIsStaff = false,
}: {
  form: IntakeForm;
  readOnly: boolean;
  h: IntakeModuleHandlers;
  viewerIsStaff?: boolean;
}) {
  const familyMode = intendedUseIsFamily(form.intended_use);
  // Fix #1: show ONE blank person form by default (never a section that opens
  // with only an Add button). The first edit materialises it into form.persons.
  const persons = form.persons.length ? form.persons : [blankPerson()];

  function setPerson(i: number, patch: Partial<PersonInput>) {
    const next = persons.slice();
    next[i] = { ...next[i], ...patch };
    h.patch({ persons: next });
  }
  function addPerson() {
    h.patch({ persons: [...persons, blankPerson()] });
  }
  function removePerson(i: number) {
    h.patch({ persons: persons.filter((_, j) => j !== i) });
  }

  if (readOnly) {
    return (
      <Box title="People" note="Saved">
        {persons.length === 0 && <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>No people added.</p>}
        {persons.map((p) => (
          <ReadRow
            key={p.id}
            k={p.name || '—'}
            v={[
              p.role ? participantLabel(p.role) : '',
              familyMode
                ? p.relation_to_client ? relationToClientLabel(p.relation_to_client) : ''
                : p.work_role ? workAxisLabel(p.work_role) : '',
              p.is_breadwinner ? 'bread-earner' : '',
              p.astro_in_scope !== false ? 'astrology on' : 'astrology off',
              (p.health_concerns || '').trim() ? `health: ${p.health_concerns!.trim()}` : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          />
        ))}
        <AutoChart persons={persons} familyMode={familyMode} />
      </Box>
    );
  }

  return (
    <Box title="People" note="Who uses the space?" allowOverflow>
      {persons.length === 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Add the people who live or work here.</p>
      )}
      {persons.map((p, i) => (
        <L1APersonCard
          key={p.id}
          p={p}
          familyMode={familyMode}
          viewerIsStaff={viewerIsStaff}
          onChange={(patch) => setPerson(i, patch)}
          onRemove={() => removePerson(i)}
        />
      ))}
      <Btn3 intent="ink" size="sm" onClick={addPerson}>
        + Add another person
      </Btn3>
      <AutoChart persons={persons} familyMode={familyMode} />
    </Box>
  );
}

function L1APersonCard({
  p,
  familyMode,
  viewerIsStaff,
  onChange,
  onRemove,
}: {
  p: PersonInput;
  familyMode: boolean;
  viewerIsStaff: boolean;
  onChange: (patch: Partial<PersonInput>) => void;
  onRemove: () => void;
}) {
  const astroOn = p.astro_in_scope !== false;
  // Relation / role options for the family-vs-work axis.
  const relOptions = familyMode
    ? RELATION_TO_CLIENT_GROUPS.flatMap((g) => g.values.map(relationToClientLabel))
    : WORK_ROLE_GROUPS.flatMap((g) => g.values.map(workAxisLabel));

  // Fix #4: auto-fetch place-of-birth coordinates from City + State + Country,
  // reusing the SAME geocoder the astrology app uses (Nominatim). Runs on blur
  // of the place fields; only fills when coordinates are still empty (manual
  // paste always wins / can override). Editable afterwards.
  const [geoBusy, setGeoBusy] = useState(false);
  async function autoGeocodePob() {
    const city = (p.pob_city || '').trim();
    if (!city) return;
    // Don't clobber a coordinate the client already entered.
    if (coordPairOf(p).trim()) return;
    setGeoBusy(true);
    try {
      const country = (p.pob_country || '').trim() || 'India';
      const r = await geocode({
        place: city,
        state: (p.pob_state || '').trim() || undefined,
        country,
        country_code: countryCodeHint(country),
      });
      if (r && !coordPairOf(p).trim()) {
        onChange({ lat: r.lat.toFixed(6), lon: r.lon.toFixed(6) });
      }
    } finally {
      setGeoBusy(false);
    }
  }

  return (
    <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <Field label="Name">
          <input style={fieldStyle} value={p.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Person's name" />
        </Field>
        <Field label="Who they are">
          <select style={fieldStyle} value={p.role || ''} onChange={(e) => onChange({ role: e.target.value })}>
            <option value="">Select…</option>
            {PARTICIPANT_TYPES_CLIENT.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
            {viewerIsStaff && (
              <option value={PARTICIPANT_TYPE_STAFF.value}>{PARTICIPANT_TYPE_STAFF.label}</option>
            )}
            {p.role &&
              ![...PARTICIPANT_TYPES_CLIENT.map((t) => t.value), PARTICIPANT_TYPE_STAFF.value].includes(p.role) && (
                <option value={p.role}>{p.role}</option>
              )}
          </select>
        </Field>
        <Field label={familyMode ? 'Relation to you' : 'Role in the organisation'} optional>
          <Combobox
            value={familyMode ? relationLabelOf(p.relation_to_client) : workLabelOf(p.work_role)}
            onChange={(v) =>
              familyMode ? onChange({ relation_to_client: v }) : onChange({ work_role: v })
            }
            options={relOptions}
            allowAdd
            placeholder="Type to pick"
            ariaLabel={familyMode ? 'Relation to you' : 'Role in the organisation'}
          />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', margin: '10px 0' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--ink)', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!p.is_breadwinner} onChange={(e) => onChange({ is_breadwinner: e.target.checked })} />
          Bread-earner of the family
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--ink)', cursor: 'pointer' }}>
          <input type="checkbox" checked={astroOn} onChange={(e) => onChange({ astro_in_scope: e.target.checked })} />
          <b>Include per-person astrology</b>
          <span style={{ color: 'var(--ink-mute)', fontWeight: 500 }}>(on by default — birth details required unless switched off)</span>
        </label>
      </div>

      {astroOn && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            <Field label="">
              <L1ALabel required fmt="DD MM YYYY">Date of birth</L1ALabel>
              <DateMask value={p.dob || ''} onChange={(v) => onChange({ dob: v })} ariaLabel="Date of birth" />
            </Field>
            <Field label="">
              <L1ALabel required fmt="HH:MM · 24-hr">Time of birth</L1ALabel>
              <TimeMask value={p.time_of_birth || ''} onChange={(v) => onChange({ time_of_birth: v })} ariaLabel="Time of birth" />
            </Field>
          </div>
          <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: 12, marginTop: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
              Place of birth{reqMark}
            </div>
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}
              onBlur={(e) => {
                // Auto-geocode only when focus leaves the whole City/State/Country
                // group (not when tabbing between its own fields).
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  void autoGeocodePob();
                }
              }}
            >
              <Field label="">
                <L1ALabel required>City</L1ALabel>
                <Combobox
                  value={p.pob_city || ''}
                  onChange={(v) => onChange({ pob_city: v })}
                  options={POB_CITY_OPTIONS}
                  allowAdd
                  placeholder="e.g. Nagpur"
                  ariaLabel="Birth city"
                />
              </Field>
              <Field label="State" optional>
                <input style={fieldStyle} value={p.pob_state || ''} onChange={(e) => onChange({ pob_state: e.target.value })} placeholder="e.g. Maharashtra" />
              </Field>
              <Field label="">
                <L1ALabel required>Country</L1ALabel>
                <Combobox
                  value={p.pob_country || ''}
                  onChange={(v) => onChange({ pob_country: v })}
                  options={searchCountries(p.pob_country || '')}
                  placeholder="Type to search…"
                  ariaLabel="Birth country"
                />
              </Field>
            </div>
            <Field
              label=""
              hint={
                geoBusy
                  ? 'Fetching coordinates from City / State / Country…'
                  : 'Auto-filled from City + State + Country (same geocoding as the astrology app). Editable — paste manually if needed; decimal (21.146633, 79.088860) or DMS.'
              }
            >
              <L1ALabel required fmt={geoBusy ? 'fetching…' : 'auto-filled from city'}>Coordinates</L1ALabel>
              <input
                style={fieldStyle}
                value={coordPairOf(p)}
                onChange={(e) => {
                  const pair = parseCoordPair(e.target.value);
                  // Store the raw string in lat (as combined) — we keep lat/lon
                  // canonical; persist parsed into lat/lon, keep raw display via lat string.
                  if (pair) onChange({ lat: String(pair.lat), lon: String(pair.lon) });
                  else onChange({ lat: e.target.value, lon: '' });
                }}
                placeholder="Auto-fills from City + State + Country"
              />
            </Field>
          </div>
        </>
      )}

      {/* STANDARD per-person Health module (2026-06-23) — directly below Place of
          birth. A per-person attribute read alongside the person's astrology;
          optional & private. Always shown (independent of the astro toggle) so a
          health note can be captured even when astrology is switched off. */}
      <div style={{ marginTop: 10 }}>
        <Field
          label=""
          hint="Per-person & read alongside their astrology. Shared only if you wish. We never diagnose or prescribe."
        >
          <L1ALabel optional>Health concerns for this person</L1ALabel>
          <input
            style={fieldStyle}
            value={p.health_concerns || ''}
            onChange={(e) => onChange({ health_concerns: e.target.value })}
            placeholder="e.g. recurring anxiety, sleep trouble, BP — anything you'd like the reading to keep in view"
            aria-label="Health concerns for this person"
          />
        </Field>
      </div>

      <div style={{ marginTop: 8, textAlign: 'right' }}>
        <Btn3 intent="ghost" size="sm" onClick={onRemove} aria-label="Remove person">
          Remove person
        </Btn3>
      </div>
    </div>
  );
}

/** Common birth-city options (combobox; client can add their own). */
const POB_CITY_OPTIONS: string[] = [
  'Nagpur', 'Indore', 'Mumbai', 'Delhi', 'Pune', 'Bengaluru', 'Jaipur', 'Bhopal', 'Ahmedabad', 'Hyderabad', 'Kolkata', 'Chennai',
];

/** Resolve the displayed relation label (controlled label, else the raw value). */
function relationLabelOf(v: string | undefined): string {
  if (!v) return '';
  return RELATION_TO_CLIENT_VALUES.includes(v as (typeof RELATION_TO_CLIENT_VALUES)[number])
    ? relationToClientLabel(v)
    : v;
}
function workLabelOf(v: string | undefined): string {
  if (!v) return '';
  return WORK_ROLE_VALUES.includes(v as (typeof WORK_ROLE_VALUES)[number]) ? workAxisLabel(v) : v;
}

/** The combined "lat, long" string for a person's stored lat/lon. */
function coordPairOf(p: PersonInput): string {
  const latN = Number(p.lat);
  const lonN = Number(p.lon);
  if (Number.isFinite(latN) && Number.isFinite(lonN) && (p.lon || '').trim()) {
    return formatCoordPair(latN, lonN) || (p.lat || '');
  }
  return p.lat || '';
}

/**
 * Fix #5 — the auto household / org chart, read-only, derived from the people +
 * their relation_to_client (family) or work_role / reports_to (org).
 *
 * FAMILY (residence / mixed): real kinship layout —
 *   · couples sit SIDE-BY-SIDE joined by a marriage link (⚭) at the same level;
 *   · children hang BELOW the couple on a shared horizontal SIBLING BAR, each
 *     son / daughter side-by-side (son-/daughter-in-law paired beside the
 *     relevant child);
 *   · generations stack vertically — grandparents / parents above Self, Self's
 *     generation, children below, grandchildren two below.
 *
 * ORG (work uses): top-down reporting hierarchy by work-role seniority
 *   (Founder/Promoter → Director → Manager → Employee) or explicit reports_to;
 *   peers side-by-side; external parties (consultant/vendor/contractor) on a
 *   dotted side-link.
 *
 * v1 renders the couple-pairing + children-sibling-bar correctly and supports
 * the other generations as the data provides them.
 */
function AutoChart({ persons, familyMode }: { persons: PersonInput[]; familyMode: boolean }) {
  const named = persons.filter((p) => (p.name || '').trim());
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
        {familyMode ? 'Household chart' : 'Organisation chart'}{' '}
        <span style={{ background: 'var(--ok-wash, #dcfce7)', color: 'var(--ok-deep, #166534)', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase' }}>
          auto
        </span>
      </div>
      <div
        style={{
          background: 'var(--surface)',
          border: '1px dashed var(--rule-strong)',
          borderRadius: 'var(--r-sm)',
          padding: 16,
          color: 'var(--ink-mute)',
          overflowX: 'auto',
        }}
      >
        {named.length === 0 ? (
          <div style={{ fontSize: 12, textAlign: 'center' }}>
            Builds itself from the people above + their relation / work-role — no one types this.
          </div>
        ) : familyMode ? (
          <FamilyChart persons={named} />
        ) : (
          <OrgChart persons={named} />
        )}
      </div>
    </div>
  );
}

/** Kinship bucket for a relation value, used to place a person by generation. */
type Generation = 'grandparents' | 'parents' | 'self' | 'children' | 'grandchildren' | 'siblings' | 'other';
function generationOf(rel: string | undefined): Generation {
  switch (rel) {
    case 'grandfather':
    case 'grandmother':
      return 'grandparents';
    case 'father':
    case 'mother':
    case 'father_in_law':
    case 'mother_in_law':
      return 'parents';
    case 'self':
    case 'spouse':
    case 'business_partner':
      return 'self';
    case 'son':
    case 'daughter':
    case 'son_in_law':
    case 'daughter_in_law':
      return 'children';
    case 'grandson':
    case 'granddaughter':
      return 'grandchildren';
    case 'elder_brother':
    case 'younger_brother':
    case 'elder_sister':
    case 'younger_sister':
    case 'brother_in_law':
    case 'sister_in_law':
      return 'siblings';
    default:
      return 'other';
  }
}

export function FamilyChart({ persons }: { persons: PersonInput[] }) {
  // Self = the person explicitly marked self, else the breadwinner, else first.
  const self =
    persons.find((p) => p.relation_to_client === 'self') ||
    persons.find((p) => p.is_breadwinner) ||
    persons[0];
  const spouse = persons.find((p) => p !== self && p.relation_to_client === 'spouse');

  const used = new Set<string>([self.id]);
  if (spouse) used.add(spouse.id);

  const inGen = (g: Generation) =>
    persons.filter((p) => !used.has(p.id) && generationOf(p.relation_to_client) === g);

  const grandparents = inGen('grandparents');
  const parents = inGen('parents');
  const siblings = inGen('siblings');
  grandparents.forEach((p) => used.add(p.id));
  parents.forEach((p) => used.add(p.id));
  siblings.forEach((p) => used.add(p.id));

  // Children — pair each son/daughter with its in-law spouse where present.
  const childRels = persons.filter((p) => !used.has(p.id) && generationOf(p.relation_to_client) === 'children');
  const blood = childRels.filter((p) => p.relation_to_client === 'son' || p.relation_to_client === 'daughter');
  const inlaws = childRels.filter((p) => p.relation_to_client === 'son_in_law' || p.relation_to_client === 'daughter_in_law');
  childRels.forEach((p) => used.add(p.id));
  const childUnits: { child: PersonInput; partner?: PersonInput }[] = [];
  const inlawPool = inlaws.slice();
  for (const child of blood) {
    // a son pairs with a daughter-in-law; a daughter pairs with a son-in-law
    const wantInlaw = child.relation_to_client === 'son' ? 'daughter_in_law' : 'son_in_law';
    const pi = inlawPool.findIndex((q) => q.relation_to_client === wantInlaw);
    const partner = pi >= 0 ? inlawPool.splice(pi, 1)[0] : undefined;
    childUnits.push({ child, partner });
  }
  // Any unmatched in-laws (no matching blood child) become their own units.
  for (const il of inlawPool) childUnits.push({ child: il });

  const grandchildren = inGen('grandchildren');
  grandchildren.forEach((p) => used.add(p.id));
  const others = persons.filter((p) => !used.has(p.id)); // anyone unplaced

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'fit-content' }}>
      {grandparents.length > 0 && (
        <>
          <PeerRow people={grandparents} />
          <VLine />
        </>
      )}
      {parents.length > 0 && (
        <>
          <PeerRow people={parents} />
          <VLine />
        </>
      )}

      {/* Self & spouse — side by side, marriage link */}
      <Couple a={self} b={spouse} rootA />

      {siblings.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--ink-mute)', textAlign: 'center', marginBottom: 4 }}>Siblings</div>
          <PeerRow people={siblings} />
        </div>
      )}

      {(childUnits.length > 0) && (
        <>
          <VLine />
          <SiblingBar units={childUnits} />
          {grandchildren.length > 0 && (
            <>
              <VLine />
              <PeerRow people={grandchildren} />
            </>
          )}
        </>
      )}

      {others.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--ink-mute)', textAlign: 'center', marginBottom: 4 }}>Also in the household</div>
          <PeerRow people={others} />
        </div>
      )}
    </div>
  );
}

/** A married/partnered pair side-by-side, joined by a ⚭ link. */
function Couple({ a, b, rootA }: { a: PersonInput; b?: PersonInput; rootA?: boolean }) {
  if (!b) return <ChartNode label={a.name} role={relationLabelOf(a.relation_to_client)} root={rootA} />;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <ChartNode label={a.name} role={relationLabelOf(a.relation_to_client)} root={rootA} />
      <div style={{ position: 'relative', width: 26, height: 0, borderTop: '2px solid var(--rule-strong)' }}>
        <span style={{ position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', fontSize: 11, color: 'var(--ink-mute)', background: 'var(--surface)', padding: '0 1px' }}>
          ⚭
        </span>
      </div>
      <ChartNode label={b.name} role={relationLabelOf(b.relation_to_client)} />
    </div>
  );
}

/** The horizontal sibling bar with each child unit hanging off it. */
function SiblingBar({ units }: { units: { child: PersonInput; partner?: PersonInput }[] }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 20,
        justifyContent: 'center',
        flexWrap: 'wrap',
        borderTop: '2px solid var(--rule-strong)',
        paddingTop: 14,
      }}
    >
      {units.map(({ child, partner }) => (
        <div key={child.id} style={{ position: 'relative' }}>
          {/* the drop from the sibling bar to this unit */}
          <div style={{ position: 'absolute', top: -14, left: '50%', width: 2, height: 14, background: 'var(--rule-strong)' }} />
          {partner ? (
            <Couple a={child} b={partner} />
          ) : (
            <ChartNode label={child.name} role={relationLabelOf(child.relation_to_client)} />
          )}
        </div>
      ))}
    </div>
  );
}

/** Peers on one level, side by side (no marriage link). */
function PeerRow({ people }: { people: PersonInput[] }) {
  return (
    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
      {people.map((p) => (
        <ChartNode key={p.id} label={p.name} role={relationLabelOf(p.relation_to_client)} />
      ))}
    </div>
  );
}

function VLine() {
  return <div style={{ width: 2, height: 16, background: 'var(--rule-strong)' }} />;
}

/**
 * Org chart — top-down by reports_to where present, else by work-role seniority.
 * External parties (consultant/vendor/contractor) sit on a dotted side-link.
 *
 * Exported so the Group record (Families) can mount the SAME reporting-hierarchy
 * chart it draws at intake — driven by each person's `work_role` / `reports_to`
 * (mapped from the group members). Pure + prop-driven; the caller is responsible
 * for handing it a cycle-free / dangle-free person set.
 *
 * The level layout is the pure, unit-tested `computeOrgLevels` (ring-safe: a
 * no-root reporting ring falls back to seniority so no member is dropped).
 */
export function OrgChart({ persons }: { persons: PersonInput[] }) {
  const externals = persons.filter((p) => EXTERNAL_ROLES.has(p.work_role || ''));
  const internals = persons.filter((p) => !EXTERNAL_ROLES.has(p.work_role || ''));
  const levels = computeOrgLevels(internals);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'fit-content' }}>
      {levels.map((row, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {i > 0 && <VLine />}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {row.map((p) => (
              <ChartNode key={p.id} label={p.name} role={workLabelOf(p.work_role)} root={i === 0} />
            ))}
          </div>
        </div>
      ))}
      {externals.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '2px dotted var(--rule-strong)' }}>
          <div style={{ fontSize: 10, color: 'var(--ink-mute)', textAlign: 'center', marginBottom: 4 }}>
            External (consultant / vendor / contractor)
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {externals.map((p) => (
              <ChartNode key={p.id} label={p.name} role={workLabelOf(p.work_role)} dashed />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ChartNode({ label, role, root, dashed }: { label: string; role?: string; root?: boolean; dashed?: boolean }) {
  return (
    <div
      style={{
        background: root ? 'var(--ink)' : 'var(--surface)',
        color: root ? '#fff' : 'var(--ink)',
        border: dashed ? '1px dashed var(--rule-strong)' : '1px solid var(--rule-strong)',
        borderRadius: 'var(--r-sm)',
        padding: '6px 12px',
        fontSize: 12,
        fontWeight: 600,
        textAlign: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      {label || '—'}
      {role && <span style={{ display: 'block', fontSize: 10, fontWeight: 500, opacity: 0.85 }}>{role}</span>}
    </div>
  );
}

/* ───────────────────────── SCAFFOLD (generic) ───────────────────────── */

function ScaffoldModule({
  def,
  readOnly,
  h,
  serviceTier,
}: {
  def: ModuleDef;
  readOnly: boolean;
  h: IntakeModuleHandlers;
  serviceTier?: string;
}) {
  // L1C/L2C bright line (spec §3.3): on a report-only drawing tier (L1C) the
  // architect-contact fields of `drawings_upload` are HIDDEN — it's an independent
  // review, we do NOT contact the architect. They show when architectCoordination
  // is true (L2C). Driven by the per-tier flags config.
  const hideArchitect = def.key === 'drawings_upload' && hidesArchitectContact(serviceTier || '');
  const fields = (def.scaffoldFields || []).filter(
    (f) => !(hideArchitect && (f.name === 'architect_name' || f.name === 'architect_contact')),
  );
  if (readOnly) {
    return (
      <Box title={def.label} note="Saved">
        {fields.length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Nothing captured.</p>
        )}
        {fields.map((f) => {
          const v = h.getExtra(def.key, f.name);
          return (
            <ReadRow
              key={f.name}
              k={f.label}
              v={typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v || '')}
            />
          );
        })}
      </Box>
    );
  }
  return (
    <section className="box">
      <header className="box-h">
        <span>
          {def.label}
          {SCAFFOLD_BADGE}
        </span>
        {def.blurb && <span className="note">{def.blurb}</span>}
      </header>
      <div className="box-body">
        {hideArchitect && (
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--accent-deep, #1e40af)',
              background: 'var(--accent-wash, #eff6ff)',
              borderLeft: '3px solid var(--accent, #2563eb)',
              borderRadius: '0 var(--r-sm) var(--r-sm) 0',
              padding: '8px 12px',
              marginBottom: 12,
            }}
          >
            <b>L1C is an independent review</b> — we don't contact your architect. Need us to
            coordinate the fixes with them? That's the Integration Path · Samanvaya (L2C).
          </div>
        )}
        {fields.map((f) => (
          <ScaffoldFieldInput key={f.name} moduleKey={def.key} field={f} h={h} />
        ))}
      </div>
    </section>
  );
}

function ScaffoldFieldInput({
  moduleKey,
  field,
  h,
}: {
  moduleKey: string;
  field: ScaffoldField;
  h: IntakeModuleHandlers;
}) {
  const raw = h.getExtra(moduleKey, field.name);
  const sval = typeof raw === 'string' ? raw : '';
  const bval = typeof raw === 'boolean' ? raw : false;
  const set = (v: string | boolean) => h.setExtra(moduleKey, field.name, v);

  if (field.kind === 'checkbox') {
    return (
      <Field label="" hint={field.hint}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={bval} onChange={(e) => set(e.target.checked)} />
          {field.label}
        </label>
      </Field>
    );
  }
  if (field.kind === 'textarea') {
    return (
      <Field label={field.label} optional hint={field.hint}>
        <textarea
          style={{ ...fieldStyle, minHeight: 56, resize: 'vertical' }}
          value={sval}
          onChange={(e) => set(e.target.value)}
        />
      </Field>
    );
  }
  if (field.kind === 'select') {
    return (
      <Field label={field.label} optional hint={field.hint}>
        <select style={fieldStyle} value={sval} onChange={(e) => set(e.target.value)}>
          <option value="">Select…</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>
    );
  }
  // text | date
  return (
    <Field label={field.label} optional hint={field.hint}>
      <input
        type={field.kind === 'date' ? 'date' : 'text'}
        style={fieldStyle}
        value={sval}
        onChange={(e) => set(e.target.value)}
      />
    </Field>
  );
}
